// program_templates.js — the program library: pure data + builders (no React).
//
//   TEMPLATES                       53 structured programs, beginner → advanced, 2–6 days/week.
//                                   Exercises may declare a progression scheme (progression.js):
//                                   linear, percent (training max waves), rpe_topset, gzcl, rp_sets;
//                                   templates with a week structure carry `cycle`.
//   recommendTemplates(profile)     ranks every template by fit → [{ template, score, fit, reasons[] }]
//   buildProgramFromTemplate(t, { exercises, catalog, profile, units, state })
//                                   → { program, newExercises, missing }
//   resolveTemplateExercise(name, exercises, catalog)
//   templateVolume(t, exercises, catalog) → weekly hard sets per muscle
//   equipmentForPreset(preset)      'gym' | 'dumbbells' | 'bodyweight' → equipment tags
//
// Every exercise is referenced by its exact library/catalog name. Names resolve to
// a built-in exercise first (so history lines up with x_* ids), then to the
// 870-entry catalog (fx_* ids, which the caller copies into state.exercises via
// actions.ensureExercises(newExercises) BEFORE adding the program).
// Weights in the produced program are pounds (the app's storage unit).

import { resolveExercise } from './coach_tools.js';
import { catalogToExercise } from './exercise_catalog.js';
import { exerciseHistory, loadFor, musclesFor, toLoggedLoad } from './analytics.js';
import { WAVES, roundTo, missingTMs, schemeOf, loadable, equipmentKind } from './progression.js';

// ——— Vocabulary ————————————————————————————————————————————————————————

export const GOALS = [
  { id: 'hypertrophy',   label: 'Build muscle',        sub: 'Size and shape, moderate reps' },
  { id: 'strength',      label: 'Get stronger',        sub: 'Heavier lifts, lower reps' },
  { id: 'powerbuilding', label: 'Strength and size',   sub: 'Heavy compounds plus volume' },
  { id: 'general',       label: 'Fitness and health',  sub: 'Feel better, move well, stay consistent' },
];
export const LEVELS = [
  { id: 'new',          label: 'Brand new',     sub: 'Never followed a lifting routine' },
  { id: 'beginner',     label: 'Beginner',      sub: 'Under a year of regular training' },
  { id: 'intermediate', label: 'Intermediate',  sub: '1–3 years, know the main lifts' },
  { id: 'advanced',     label: 'Advanced',      sub: '3+ years, progress comes slowly' },
];
export const LEVEL_LABEL = { new: 'Beginner', beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
const GOAL_PHRASE = { hypertrophy: 'building muscle', strength: 'getting stronger', powerbuilding: 'strength and size', general: 'general fitness' };
export const GOAL_LABEL = { hypertrophy: 'Muscle', strength: 'Strength', powerbuilding: 'Strength + size', general: 'General fitness' };

// Equipment tags a template can require.
export const EQUIPMENT_TAGS = [
  { id: 'barbell',  label: 'Barbell + rack' },
  { id: 'dumbbell', label: 'Dumbbells' },
  { id: 'bench',    label: 'Bench' },
  { id: 'cable',    label: 'Cable station' },
  { id: 'machine',  label: 'Machines' },
  { id: 'pullup',   label: 'Pull-up bar' },
  { id: 'kettlebell', label: 'Kettlebell' },
  { id: 'bands',    label: 'Bands' },
];
export const EQUIPMENT_PRESETS = [
  { id: 'gym',        label: 'Full gym',       sub: 'Barbells, dumbbells, cables, machines' },
  { id: 'dumbbells',  label: 'Home dumbbells', sub: 'A pair of adjustable dumbbells and a bench' },
  { id: 'bodyweight', label: 'Bodyweight',     sub: 'No equipment — floor, chair, a sturdy table' },
  { id: 'custom',     label: 'Let me pick',    sub: 'Choose exactly what you have' },
];
export function equipmentForPreset(preset) {
  if (preset === 'gym') return ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'];
  if (preset === 'dumbbells') return ['dumbbell', 'bench'];
  if (preset === 'bodyweight') return [];
  return null; // custom → caller supplies the list
}
const EQUIP_PHRASE = {
  barbell: 'a barbell', dumbbell: 'dumbbells', bench: 'a bench', cable: 'cables', machine: 'machines',
  pullup: 'a pull-up bar', kettlebell: 'a kettlebell', bands: 'bands',
};

// ——— Template DSL ————————————————————————————————————————————————————————
// E(name, sets, reps, rest, extra) — reps '5' | '8-12'; extra: { ss: 'a' (superset tag), note }

function E(x, sets, reps, rest, extra = {}) {
  const [a, b] = String(reps).split('-').map(Number);
  return { x, sets, repMin: a, repMax: b || a, rest, ...extra };
}
const D = (name, day, focus, exercises) => ({ name, day, focus, exercises });

// Progression-scheme helpers (see progression.js). Each still yields sets/repMin/
// repMax so stats, volume and the editor keep working.
//   P  — % of a training max on a named wave. cfg: { inc, bumps, tmOf: 'Lift name', tmRule }
//   L  — linear: fixed sets × reps, +inc a session, deload after failLimit misses
//   G  — tiered (GZCL-style): tier 1/2/3
//   R  — top set at RPE + back-off sets. cfg: { rpe (number | per-week array), drop }
//   M  — hypertrophy meso set ramp from mev to mrv sets, RIR 3→0
function P(x, wave, rest, cfg = {}) {
  const w = WAVES[wave];
  const sets = Math.max(...w.weeks.map(wk => wk.reduce((a, sp) => a + (sp.sets || 1), 0)));
  const reps = w.weeks[0].map(sp => sp.reps);
  return E(x, sets, `${Math.min(...reps)}-${Math.max(...reps)}`, rest, { scheme: 'percent', cfg: { wave, ...cfg } });
}
const L = (x, sets, reps, rest, cfg = {}) => E(x, sets, String(reps), rest, { scheme: 'linear', cfg: { failLimit: 3, ...cfg } });
function G(x, tier, rest, cfg = {}) {
  const st = { 1: [5, '3'], 2: [3, '10'], 3: [3, '15'] }[tier];
  return E(x, st[0], st[1], rest, { scheme: 'gzcl', cfg: { tier, ...cfg } });
}
const R = (x, topReps, backoffs, backoffReps, rest, cfg = {}) =>
  E(x, 1 + backoffs, `${Math.min(topReps, backoffReps)}-${Math.max(topReps, backoffReps)}`, rest, { scheme: 'rpe_topset', cfg: { topReps, backoffs, backoffReps, drop: 10, rpe: 8, ...cfg } });
const M = (x, mev, mrv, reps, rest, cfg = {}) => E(x, mev, reps, rest, { scheme: 'rp_sets', cfg: { mev, mrv, ...cfg } });

// Week structures (program.cycle).
const CY = {
  wave4: { repeat: true, weeks: [{ label: 'Week 1 · 5s' }, { label: 'Week 2 · 3s' }, { label: 'Week 3 · 5/3/1' }, { label: 'Deload', deload: true }] },
  meso5: { repeat: true, weeks: [{ label: 'Week 1 · 3 RIR' }, { label: 'Week 2 · 2 RIR' }, { label: 'Week 3 · 1 RIR' }, { label: 'Week 4 · 0 RIR' }, { label: 'Deload', deload: true }] },
  block6: { repeat: true, weeks: [{ label: 'Conditioning' }, { label: 'Hypertrophy' }, { label: 'Strength' }, { label: 'Heavy' }, { label: 'Peak' }, { label: 'Test' }] },
  peak12: { repeat: false, weeks: [
    { label: 'Volume 1' }, { label: 'Volume 2' }, { label: 'Volume 3' }, { label: 'Deload', deload: true },
    { label: 'Strength 1' }, { label: 'Strength 2' }, { label: 'Strength 3' }, { label: 'Deload', deload: true },
    { label: 'Peak 1' }, { label: 'Peak 2' }, { label: 'Taper', deload: true }, { label: 'Test day' },
  ] },
  rtf7: { repeat: true, weeks: [{ label: 'Week 1 · 10s' }, { label: 'Week 2 · 9s' }, { label: 'Week 3 · 8s' }, { label: 'Week 4 · 9s' }, { label: 'Week 5 · 8s' }, { label: 'Week 6 · 7s' }, { label: 'Deload', deload: true }] },
  rpe5: { repeat: true, weeks: [{ label: 'Week 1 · RPE 7' }, { label: 'Week 2 · RPE 8' }, { label: 'Week 3 · RPE 8.5' }, { label: 'Week 4 · RPE 9' }, { label: 'Deload', deload: true }] },
  rpe4: { repeat: true, weeks: [{ label: 'Week 1 · RPE 7' }, { label: 'Week 2 · RPE 8' }, { label: 'Week 3 · RPE 9' }, { label: 'Deload', deload: true }] },
  peak8: { repeat: false, weeks: [
    { label: 'Volume 1' }, { label: 'Volume 2' }, { label: 'Strength' }, { label: 'Deload', deload: true },
    { label: 'Heavy' }, { label: 'Peak' }, { label: 'Taper', deload: true }, { label: 'Test day' },
  ] },
  bench6: { repeat: true, weeks: [{ label: 'Week 1 · 5s' }, { label: 'Week 2 · 4s' }, { label: 'Week 3 · 3s' }, { label: 'Week 4 · 2s' }, { label: 'Week 5 · singles' }, { label: 'Test' }] },
};

// ——— The library ————————————————————————————————————————————————————————

export const TEMPLATES = [
  {
    id: 'fb3-beginner',
    name: '3-Day Full Body Beginner',
    tagline: 'Learn the big lifts, train everything three times a week.',
    description: 'Three full-body sessions built around one squat or hinge, one press and one pull each day, plus a little arm and core work. High frequency means fast skill gains, and every session is under an hour. Add reps until you hit the top of each range, then add weight.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general', 'hypertrophy', 'strength'],
    daysPerWeek: 3, minutes: 50, weeks: 8, split: 'Full body',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable'],
    progression: 'Double progression: when every set reaches the top of the rep range, add 5 lb (2.5 lb for upper body) next time.',
    days: [
      D('Full Body A', 'Mon', 'Squat · Bench · Row', [
        E('Back Squat', 3, '6-8', 150),
        E('Barbell Bench Press', 3, '6-8', 150),
        E('Chest-Supported Row', 3, '8-12', 120),
        E('Side Lateral Raise', 2, '12-15', 60),
        E('Plank', 3, '30-45', 60),
      ]),
      D('Full Body B', 'Wed', 'Hinge · Press · Pulldown', [
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Overhead Press', 3, '6-8', 150),
        E('Lat Pulldown (wide)', 3, '8-12', 120),
        E('Goblet Squat', 2, '10-12', 90),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
      D('Full Body C', 'Fri', 'Legs · Incline · Row', [
        E('Leg Press', 3, '10-12', 120),
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Cable Row (neutral)', 3, '8-12', 120),
        E('Seated Leg Curl', 2, '10-12', 75),
        E('Cable Triceps Pushdown', 2, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'lp-5x5',
    name: '5×5 Linear Strength',
    tagline: 'The classic barbell starter: five lifts, add weight every session.',
    description: 'Two alternating barbell workouts, three days a week. Squat every session, alternate bench and overhead press, row and deadlift. Five sets of five at the same weight; when you make all 25 reps, add weight next time. Simple, brutally effective for the first 3–6 months.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 3, minutes: 50, weeks: 12, split: 'A / B alternating',
    equipment: ['barbell', 'bench'],
    progression: 'Linear, run by the app: make all 25 reps and the lift goes up 5 lb next session (deadlift 10 lb, press 2.5 lb). Miss twice in a row and it drops 10% to build back. The squat moves every session.',
    days: [
      D('Workout A', 'Mon', 'Squat · Bench · Row', [
        L('Back Squat', 5, 5, 180, { failLimit: 2 }),
        L('Barbell Bench Press', 5, 5, 180, { failLimit: 2 }),
        L('Bent Over Barbell Row', 5, 5, 150, { failLimit: 2 }),
      ]),
      D('Workout B', 'Wed', 'Squat · Press · Deadlift', [
        L('Back Squat', 5, 5, 180, { failLimit: 2 }),
        L('Overhead Press', 5, 5, 180, { failLimit: 2, inc: 2.5 }),
        L('Conventional Deadlift', 1, 5, 240, { failLimit: 2, inc: 10 }),
      ]),
      D('Workout A', 'Fri', 'Squat · Bench · Row', [
        L('Back Squat', 5, 5, 180, { failLimit: 2 }),
        L('Barbell Bench Press', 5, 5, 180, { failLimit: 2 }),
        L('Bent Over Barbell Row', 5, 5, 150, { failLimit: 2 }),
      ]),
    ],
  },
  {
    id: 'min2',
    name: 'Minimalist 2-Day Full Body',
    tagline: 'Two efficient sessions a week that still move the needle.',
    description: 'For busy weeks: two full-body sessions that hit every major muscle with heavy compound lifts and a couple of high-value accessories. Research shows twice-weekly training keeps most of the gains of higher frequencies when effort is high.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'strength', 'hypertrophy'],
    daysPerWeek: 2, minutes: 55, weeks: 8, split: 'Full body',
    equipment: ['barbell', 'dumbbell', 'bench', 'pullup'],
    progression: 'Double progression on every lift. Take the last set of each compound close to failure (1–2 reps in reserve).',
    days: [
      D('Full Body A', 'Mon', 'Squat · Bench · Chin-up', [
        E('Back Squat', 3, '5-8', 180),
        E('Barbell Bench Press', 3, '5-8', 180),
        E('Chin-Up', 3, '6-10', 120),
        E('Stiff-Legged Dumbbell Deadlift', 2, '10-12', 90),
        E('Side Lateral Raise', 2, '12-15', 60),
      ]),
      D('Full Body B', 'Thu', 'Deadlift · Press · Row', [
        E('Conventional Deadlift', 3, '4-6', 210),
        E('Overhead Press', 3, '6-8', 150),
        E('One-Arm Dumbbell Row', 3, '8-12', 90),
        E('Bulgarian Split Squat', 2, '8-10', 90),
        E('Hanging Leg Raise', 2, '8-12', 60),
      ]),
    ],
  },
  {
    id: 'express3',
    name: '30-Minute Express Full Body',
    tagline: 'In and out in half an hour, machines and cables only.',
    description: 'Short on time or new to free weights? Machines and cables are easy to learn and safe to push hard. Paired exercises (supersets) keep sessions to about 30 minutes while hitting every muscle three times a week.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general', 'hypertrophy'],
    daysPerWeek: 3, minutes: 30, weeks: 8, split: 'Full body · supersets',
    equipment: ['machine', 'cable'],
    progression: 'When you hit the top of the range on every set, move the pin one plate heavier.',
    days: [
      D('Express A', 'Mon', 'Legs · Chest · Back', [
        E('Leg Press', 3, '10-12', 60, { ss: 'a' }),
        E('Lat Pulldown (wide)', 3, '10-12', 90, { ss: 'a' }),
        E('Machine Bench Press', 3, '10-12', 60, { ss: 'b' }),
        E('Seated Leg Curl', 3, '10-12', 90, { ss: 'b' }),
      ]),
      D('Express B', 'Wed', 'Quads · Shoulders · Row', [
        E('Leg Extensions', 3, '12-15', 60, { ss: 'a' }),
        E('Cable Row (neutral)', 3, '10-12', 90, { ss: 'a' }),
        E('Machine Shoulder Press', 3, '10-12', 60, { ss: 'b' }),
        E('Cable Crunch', 3, '12-15', 90, { ss: 'b' }),
      ]),
      D('Express C', 'Fri', 'Legs · Chest · Arms', [
        E('Hack Squat', 3, '10-12', 60, { ss: 'a' }),
        E('Pec Deck', 3, '12-15', 90, { ss: 'a' }),
        E('Cable Triceps Pushdown', 3, '10-12', 45, { ss: 'b' }),
        E('Standing Biceps Cable Curl', 3, '10-12', 90, { ss: 'b' }),
      ]),
    ],
  },
  {
    id: 'db3',
    name: 'Dumbbell-Only 3-Day',
    tagline: 'A complete home program with just dumbbells and a bench.',
    description: 'Full-body sessions designed around adjustable dumbbells. Single-leg work and slower tempos make moderate loads challenging, so you keep progressing even when the dumbbells top out. Every muscle gets 10+ hard sets a week.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 3, minutes: 45, weeks: 8, split: 'Full body',
    equipment: ['dumbbell', 'bench'],
    progression: 'Add reps first; when you top out the range, use the next dumbbell or slow the lowering phase to 3 seconds.',
    days: [
      D('Day A', 'Mon', 'Squat · Press · Row', [
        E('Goblet Squat', 3, '8-12', 90),
        E('Flat DB Bench Press', 3, '8-12', 90),
        E('One-Arm Dumbbell Row', 3, '8-12', 90),
        E('Seated Dumbbell Press', 2, '10-12', 75),
        E('Dumbbell Bicep Curl', 2, '10-15', 60, { ss: 'a' }),
        E('Lying Dumbbell Tricep Extension', 2, '10-15', 60, { ss: 'a' }),
      ]),
      D('Day B', 'Wed', 'Hinge · Incline · Lunge', [
        E('Stiff-Legged Dumbbell Deadlift', 3, '8-12', 90),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Dumbbell Lunges', 3, '10-12', 90),
        E('Bent Over Two-Dumbbell Row', 3, '10-12', 75),
        E('Side Lateral Raise', 3, '12-15', 60),
      ]),
      D('Day C', 'Fri', 'Split squat · Floor press · Pullover', [
        E('Bulgarian Split Squat', 3, '8-12', 90),
        E('Dumbbell Floor Press', 3, '8-12', 90),
        E('Bent-Arm Dumbbell Pullover', 3, '10-12', 75),
        E('Single Leg Glute Bridge', 3, '10-15', 60),
        E('Hammer Curl', 2, '10-12', 60, { ss: 'a' }),
        E('Reverse Flyes', 2, '12-15', 60, { ss: 'a' }),
      ]),
    ],
  },
  {
    id: 'db4-ul',
    name: 'Dumbbell Upper/Lower 4-Day',
    tagline: 'More volume for home lifters who have outgrown full body.',
    description: 'Four sessions split into upper and lower days, using only dumbbells and an adjustable bench. Each muscle is trained twice a week with enough exercise variety to keep the joints happy.',
    level: 'intermediate', levels: ['beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 4, minutes: 50, weeks: 8, split: 'Upper / Lower',
    equipment: ['dumbbell', 'bench'],
    progression: 'Double progression; when you outgrow a dumbbell, add a set or a 3-second lowering phase before jumping weight.',
    days: [
      D('Upper A', 'Mon', 'Flat press · Row · Shoulders', [
        E('Flat DB Bench Press', 4, '6-10', 120),
        E('One-Arm Dumbbell Row', 4, '8-12', 90),
        E('Seated Dumbbell Press', 3, '8-12', 90),
        E('Side Lateral Raise', 3, '12-20', 60),
        E('Incline Dumbbell Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Lying Dumbbell Tricep Extension', 3, '10-12', 60, { ss: 'a' }),
      ]),
      D('Lower A', 'Tue', 'Squat · Hinge · Calves', [
        E('Goblet Squat', 4, '8-12', 120),
        E('Stiff-Legged Dumbbell Deadlift', 3, '8-12', 120),
        E('Dumbbell Step Ups', 3, '10-12', 90),
        E('Standing Dumbbell Calf Raise', 4, '12-15', 60),
        E('Dead Bug', 3, '10-12', 45),
      ]),
      D('Upper B', 'Thu', 'Incline · Pullover · Arms', [
        E('Incline Dumbbell Press', 4, '8-12', 120),
        E('Bent Over Two-Dumbbell Row', 4, '8-12', 90),
        E('Arnold Dumbbell Press', 3, '10-12', 90),
        E('Dumbbell Flyes', 3, '12-15', 60),
        E('Reverse Flyes', 3, '12-15', 60, { ss: 'a' }),
        E('Hammer Curl', 3, '10-12', 60, { ss: 'a' }),
      ]),
      D('Lower B', 'Fri', 'Split squat · Hinge · Glutes', [
        E('Bulgarian Split Squat', 4, '8-12', 120),
        E('Dumbbell Lunges', 3, '10-12', 90),
        E('Stiff-Legged Dumbbell Deadlift', 3, '10-12', 90),
        E('Single Leg Glute Bridge', 3, '12-15', 60),
        E('Standing Dumbbell Calf Raise', 3, '12-15', 60),
      ]),
    ],
  },
  {
    id: 'bw3',
    name: 'Bodyweight Home 3-Day',
    tagline: 'No equipment, no excuses — a real progression plan at home.',
    description: 'Three full-body sessions using only your bodyweight, a chair and a sturdy table. Exercises progress from easier to harder variations (incline push-ups → push-ups → feet-elevated) so you keep getting stronger without any weights.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general', 'hypertrophy'],
    daysPerWeek: 3, minutes: 35, weeks: 8, split: 'Full body',
    equipment: [],
    progression: 'When you hit the top of the range on every set, move to the harder variation (or add a set). Keep 1–2 reps in reserve.',
    days: [
      D('Day A', 'Mon', 'Push-up · Squat · Row', [
        E('Pushups', 3, '8-15', 75),
        E('Bodyweight Squat', 3, '15-20', 75),
        E('Inverted Row', 3, '8-12', 75, { note: 'Under a sturdy table, or a bar set at hip height.' }),
        E('Butt Lift (Bridge)', 3, '12-20', 60),
        E('Plank', 3, '30-60', 45),
      ]),
      D('Day B', 'Wed', 'Lunge · Dip · Core', [
        E('Bodyweight Walking Lunge', 3, '10-12', 75),
        E('Incline Push-Up', 3, '12-20', 60),
        E('Bench Dips', 3, '8-15', 60, { note: 'Hands on a chair or couch edge.' }),
        E('Hyperextensions With No Hyperextension Bench', 3, '12-15', 60),
        E('Dead Bug', 3, '10-12', 45),
      ]),
      D('Day C', 'Fri', 'Decline push-up · Single leg · Row', [
        E('Push-Ups With Feet Elevated', 3, '6-12', 75),
        E('Single Leg Glute Bridge', 3, '10-15', 60),
        E('Step-up with Knee Raise', 3, '10-12', 60),
        E('Inverted Row', 3, '8-12', 75),
        E('Reverse Crunch', 3, '10-15', 45),
      ]),
    ],
  },
  {
    id: 'ul4-hyp',
    name: '4-Day Upper/Lower Hypertrophy',
    tagline: 'Each muscle twice a week with smart volume — the all-rounder.',
    description: 'The most popular intermediate structure for a reason: four sessions, every muscle trained twice, 10–16 hard sets per muscle per week. Heavier compounds lead each day, followed by machine and cable work that is easy to push close to failure.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general', 'powerbuilding'],
    daysPerWeek: 4, minutes: 60, weeks: 10, split: 'Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Double progression with 1–2 reps in reserve on compounds and 0–1 on isolation work. Deload every 6–8 weeks or when the coach flags fatigue.',
    days: [
      D('Upper A', 'Mon', 'Bench · Row · Shoulders', [
        E('Barbell Bench Press', 3, '6-8', 180),
        E('Chest-Supported Row', 3, '8-10', 150),
        E('Standing DB Shoulder Press', 3, '8-10', 120),
        E('Lat Pulldown (wide)', 3, '10-12', 120),
        E('Cable Lateral Raise', 3, '12-15', 60),
        E('EZ-Bar Curl', 2, '8-12', 75, { ss: 'a' }),
        E('Cable Triceps Pushdown', 2, '10-12', 75, { ss: 'a' }),
      ]),
      D('Lower A', 'Tue', 'Squat · RDL · Calves', [
        E('Back Squat', 3, '5-8', 210),
        E('Romanian Deadlift', 3, '8-10', 180),
        E('Leg Press', 3, '10-12', 150),
        E('Seated Leg Curl', 3, '10-12', 90),
        E('Standing Calf Raise', 3, '10-12', 75),
        E('Cable Crunch', 3, '10-15', 60),
      ]),
      D('Upper B', 'Thu', 'Pull-up · Incline · Arms', [
        E('Weighted Pull-up', 3, '6-8', 180),
        E('Incline Dumbbell Press', 3, '8-10', 150),
        E('Cable Row (neutral)', 3, '10-12', 120),
        E('Machine Shoulder Press', 2, '10-12', 120),
        E('Pec Deck', 2, '12-15', 75),
        E('Preacher Curl', 2, '10-12', 75, { ss: 'a' }),
        E('Overhead Cable Extension', 2, '10-12', 75, { ss: 'a' }),
      ]),
      D('Lower B', 'Fri', 'Deadlift · Split squat · Glutes', [
        E('Conventional Deadlift', 3, '4-6', 240),
        E('Bulgarian Split Squat', 3, '8-10', 120),
        E('Hip Thrust', 3, '8-10', 120),
        E('Leg Extensions', 3, '12-15', 75),
        E('Lying Leg Curl', 2, '10-12', 75),
        E('Seated Calf Raise', 3, '12-15', 60),
      ]),
    ],
  },
  {
    id: 'glute4',
    name: 'Glute & Leg Focus 4-Day',
    tagline: 'Two lower days built for glutes and legs, two lighter upper days.',
    description: 'Prioritises glutes, hamstrings and quads with heavy hip thrusts, hinges and single-leg work twice a week, while two upper-body days keep the rest of you balanced. Glutes get 16+ hard sets a week.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'], specialty: true,
    daysPerWeek: 4, minutes: 60, weeks: 10, split: 'Lower focus · Upper',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Double progression. Pause one second at the top of every hip thrust and bridge.',
    days: [
      D('Glutes & Hamstrings', 'Mon', 'Hip thrust · RDL · Abduction', [
        E('Hip Thrust', 4, '8-12', 150),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Bulgarian Split Squat', 3, '10-12', 90),
        E('Seated Leg Curl', 3, '10-12', 75),
        E('Thigh Abductor', 3, '15-20', 60),
      ]),
      D('Upper', 'Tue', 'Press · Pulldown · Shoulders', [
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Lat Pulldown (wide)', 3, '10-12', 90),
        E('Cable Row (neutral)', 3, '10-12', 90),
        E('Side Lateral Raise', 3, '12-15', 60),
        E('Cable Triceps Pushdown', 2, '10-12', 60, { ss: 'a' }),
        E('Hammer Curl', 2, '10-12', 60, { ss: 'a' }),
      ]),
      D('Quads & Glutes', 'Thu', 'Squat · Lunge · Kickback', [
        E('Back Squat', 4, '6-10', 180),
        E('Leg Press', 3, '10-15', 120),
        E('Dumbbell Lunges', 3, '10-12', 90),
        E('One-Legged Cable Kickback', 3, '12-15', 60),
        E('Leg Extensions', 3, '12-15', 60),
      ]),
      D('Upper + Glutes', 'Sat', 'Row · Shoulder press · Bridge', [
        E('Chest-Supported Row', 3, '8-12', 90),
        E('Seated Dumbbell Press', 3, '8-12', 90),
        E('Flat DB Bench Press', 3, '8-12', 90),
        E('Barbell Glute Bridge', 3, '10-15', 90),
        E('Pull Through', 3, '12-15', 60),
      ]),
    ],
  },
  {
    id: 'phul',
    name: 'Power/Hypertrophy Upper/Lower',
    tagline: 'Two heavy days and two pump days — strength and size together.',
    description: 'A PHUL-style split: Monday and Tuesday are power days built on heavy 3–5 rep compounds; Thursday and Friday are hypertrophy days with moderate weights and 8–15 reps. A great bridge from beginner programs to more advanced training.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'strength', 'hypertrophy'],
    daysPerWeek: 4, minutes: 70, weeks: 12, split: 'Power / Hypertrophy · Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Power days: add 5 lb when all sets hit the top reps. Hypertrophy days: double progression.',
    days: [
      D('Upper Power', 'Mon', 'Bench · Row · Press', [
        E('Barbell Bench Press', 4, '3-5', 210),
        E('Bent Over Barbell Row', 4, '3-5', 180),
        E('Overhead Press', 3, '5-8', 150),
        E('Weighted Pull-up', 3, '5-8', 150),
        E('Barbell Curl', 2, '6-10', 90, { ss: 'a' }),
        E('Skull Crushers', 2, '6-10', 90, { ss: 'a' }),
      ]),
      D('Lower Power', 'Tue', 'Squat · Deadlift · Leg press', [
        E('Back Squat', 4, '3-5', 240),
        E('Conventional Deadlift', 3, '3-5', 240),
        E('Leg Press', 3, '10-15', 150),
        E('Lying Leg Curl', 3, '6-10', 90),
        E('Standing Calf Raise', 4, '6-10', 75),
      ]),
      D('Upper Hypertrophy', 'Thu', 'Incline · Flyes · Rows', [
        E('Incline Dumbbell Press', 4, '8-12', 120),
        E('Cable Flies', 3, '12-15', 75),
        E('Cable Row (neutral)', 4, '8-12', 90),
        E('Lat Pulldown (wide)', 3, '10-12', 90),
        E('Side Lateral Raise', 3, '12-15', 60),
        E('Incline Dumbbell Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Overhead Cable Extension', 3, '10-12', 60, { ss: 'a' }),
      ]),
      D('Lower Hypertrophy', 'Fri', 'Front squat · Lunge · Hamstrings', [
        E('Front Squat', 4, '8-10', 150),
        E('Barbell Lunge', 3, '8-12', 120),
        E('Leg Extensions', 3, '10-15', 75),
        E('Seated Leg Curl', 3, '10-15', 75),
        E('Seated Calf Raise', 4, '10-15', 60),
      ]),
    ],
  },
  {
    id: 'gzcl-tiered',
    name: 'Tiered Linear Progression',
    tagline: 'Heavy tier-1 lift, volume tier-2, high-rep tier-3 — stages change for you.',
    description: 'A GZCLP-style week. Each day pairs one main lift for heavy triples (Tier 1) with a related lift for volume (Tier 2) and light accessories for 15+ reps (Tier 3). The app runs the stage logic: miss a rep and the next session switches to the next stage at the same weight instead of grinding the same failure.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 4, minutes: 65, weeks: 12, split: 'Tiered · Squat / Bench / Deadlift / Press',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'pullup'],
    progression: 'Run by the app. T1 5×3+ adds weight every time you make it; a miss moves to 6×2+, then 10×1+, then resets at 85% of your estimated 5RM. T2 3×10 → 3×8 → 3×6, resetting a little heavier than your last 3×10. T3 3×15+ adds weight once the last set reaches 25.',
    days: [
      D('Squat Day', 'Mon', 'T1 Squat · T2 Bench', [
        G('Back Squat', 1, 210, { inc: 10 }),
        G('Barbell Bench Press', 2, 120),
        G('Lat Pulldown (wide)', 3, 60),
        E('Hanging Leg Raise', 3, '10-15', 60),
      ]),
      D('Press Day', 'Tue', 'T1 Overhead press · T2 Deadlift', [
        G('Overhead Press', 1, 180, { inc: 5 }),
        G('Conventional Deadlift', 2, 150, { inc: 10 }),
        G('One-Arm Dumbbell Row', 3, 60),
        E('Face Pull', 3, '15-20', 60),
      ]),
      D('Bench Day', 'Thu', 'T1 Bench · T2 Squat', [
        G('Barbell Bench Press', 1, 180, { inc: 5 }),
        G('Back Squat', 2, 150, { inc: 10 }),
        G('Chest-Supported Row', 3, 60),
        E('Cable Triceps Pushdown', 3, '15', 60),
      ]),
      D('Deadlift Day', 'Fri', 'T1 Deadlift · T2 Press', [
        G('Conventional Deadlift', 1, 240, { inc: 10 }),
        G('Overhead Press', 2, 120),
        G('Lat Pulldown (wide)', 3, 60),
        E('Hammer Curl', 3, '15', 60),
      ]),
    ],
  },
  {
    id: 'pb4',
    name: 'Powerbuilding 4-Day',
    tagline: 'Strength-first compounds, bodybuilding accessories, one block.',
    description: 'Each session opens with a heavy top set and back-off sets on a big lift, then shifts to 8–15 rep accessories for size. Built for lifters who want a bigger total and a physique to match.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'strength', 'hypertrophy'],
    daysPerWeek: 4, minutes: 75, weeks: 10, split: 'Squat / Bench / Deadlift / Press',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Run by the app on a 5-week wave: one top set at RPE 7, 8, 8.5, then 9, with back-offs 10% below the load your top set says you can handle today. Your e1RM is re-estimated every session and sets next week\'s top set. Week 5 is a deload. Accessories use double progression.',
    cycle: CY.rpe5,
    days: [
      D('Squat + Lower', 'Mon', 'Squat · RDL · Quads', [
        R('Back Squat', 3, 3, 5, 240, { rpe: [7, 8, 8.5, 9, 6] }),
        E('Romanian Deadlift', 3, '6-8', 180),
        E('Hack Squat', 3, '8-12', 120),
        E('Lying Leg Curl', 3, '10-12', 75),
        E('Standing Calf Raise', 4, '8-12', 60),
      ]),
      D('Bench + Upper', 'Tue', 'Bench · Row · Arms', [
        R('Barbell Bench Press', 3, 3, 5, 210, { rpe: [7, 8, 8.5, 9, 6] }),
        E('Bent Over Barbell Row', 4, '6-8', 150),
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Lat Pulldown (wide)', 3, '10-12', 90),
        E('Skull Crushers', 3, '8-12', 75, { ss: 'a' }),
        E('EZ-Bar Curl', 3, '8-12', 75, { ss: 'a' }),
      ]),
      D('Deadlift + Lower', 'Thu', 'Deadlift · Front squat · Glutes', [
        R('Conventional Deadlift', 2, 2, 4, 270, { rpe: [7, 8, 8.5, 9, 6] }),
        E('Front Squat', 3, '6-8', 180),
        E('Hip Thrust', 3, '8-10', 120),
        E('Leg Extensions', 3, '12-15', 60),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
      D('Press + Upper', 'Fri', 'Press · Pull-up · Shoulders', [
        R('Overhead Press', 5, 3, 6, 180, { rpe: [7, 8, 8.5, 9, 6] }),
        E('Weighted Pull-up', 4, '5-8', 150),
        E('Close-Grip Barbell Bench Press', 3, '6-10', 150),
        E('Cable Lateral Raise', 4, '12-15', 60),
        E('Face Pull', 3, '15-20', 60),
        E('Hammer Curl', 3, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'bro5',
    name: '5-Day Body Part Split',
    tagline: 'One muscle group a day, lots of volume, a classic gym week.',
    description: 'Chest, back, legs, shoulders and arms each get their own day with 15–20 hard sets. Each muscle is trained once a week at high volume — enjoyable, and effective for intermediates who recover well.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy'],
    daysPerWeek: 5, minutes: 60, weeks: 8, split: 'Body part',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Double progression, last set of each exercise to 0–1 reps in reserve.',
    days: [
      D('Chest', 'Mon', 'Bench · Incline · Flyes', [
        E('Barbell Bench Press', 4, '6-10', 150),
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Machine Bench Press', 3, '10-12', 90),
        E('Cable Flies', 3, '12-15', 60),
        E('Dips - Chest Version', 2, '8-15', 90),
      ]),
      D('Back', 'Tue', 'Pull-up · Rows · Pulldown', [
        E('Weighted Pull-up', 4, '6-10', 150),
        E('Bent Over Barbell Row', 3, '8-10', 120),
        E('Chest-Supported Row', 3, '10-12', 90),
        E('Close-Grip Front Lat Pulldown', 3, '10-12', 90),
        E('Straight-Arm Pulldown', 3, '12-15', 60),
      ]),
      D('Legs', 'Wed', 'Squat · RDL · Machines', [
        E('Back Squat', 4, '6-10', 180),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Leg Press', 3, '10-15', 120),
        E('Leg Extensions', 3, '12-15', 60),
        E('Seated Leg Curl', 3, '10-12', 60),
        E('Standing Calf Raise', 4, '10-15', 60),
      ]),
      D('Shoulders', 'Thu', 'Press · Laterals · Rear delts', [
        E('Overhead Press', 4, '6-10', 150),
        E('Seated Dumbbell Press', 3, '8-12', 120),
        E('Side Lateral Raise', 4, '12-20', 60),
        E('Cable Rear Delt Fly', 3, '12-15', 60),
        E('Dumbbell Shrug', 3, '10-15', 60),
      ]),
      D('Arms', 'Fri', 'Biceps · Triceps supersets', [
        E('Barbell Curl', 3, '8-12', 75, { ss: 'a' }),
        E('Close-Grip Barbell Bench Press', 3, '8-12', 90, { ss: 'a' }),
        E('Incline Dumbbell Curl', 3, '10-12', 60, { ss: 'b' }),
        E('Overhead Cable Extension', 3, '10-12', 60, { ss: 'b' }),
        E('Hammer Curl', 3, '10-15', 60, { ss: 'c' }),
        E('Cable Triceps Pushdown', 3, '12-15', 60, { ss: 'c' }),
      ]),
    ],
  },
  {
    id: 'ppl6',
    name: 'Push/Pull/Legs 6-Day',
    tagline: 'Every muscle twice a week across six focused sessions.',
    description: 'Push (chest, shoulders, triceps), pull (back, biceps) and legs, run twice per week. The first half of the week leans heavier, the second half leans on volume. Demanding — best when sleep and food are dialled in.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'powerbuilding'],
    daysPerWeek: 6, minutes: 65, weeks: 10, split: 'Push / Pull / Legs',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Heavy days: add 5 lb when all sets hit the top of the range. Volume days: double progression. Deload when the coach flags fatigue.',
    days: [
      D('Push (heavy)', 'Mon', 'Bench · Press · Triceps', [
        E('Barbell Bench Press', 4, '5-8', 180),
        E('Overhead Press', 3, '6-8', 150),
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Cable Lateral Raise', 3, '12-15', 60),
        E('Skull Crushers', 3, '8-12', 75),
      ]),
      D('Pull (heavy)', 'Tue', 'Deadlift · Pull-up · Row', [
        E('Conventional Deadlift', 3, '4-6', 240),
        E('Weighted Pull-up', 3, '6-8', 150),
        E('Bent Over Barbell Row', 3, '8-10', 120),
        E('Face Pull', 3, '15-20', 60),
        E('EZ-Bar Curl', 3, '8-12', 75),
      ]),
      D('Legs (heavy)', 'Wed', 'Squat · RDL · Calves', [
        E('Back Squat', 4, '5-8', 210),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Leg Press', 3, '10-12', 120),
        E('Seated Leg Curl', 3, '10-12', 75),
        E('Standing Calf Raise', 4, '8-12', 60),
      ]),
      D('Push (volume)', 'Thu', 'Incline · Machine · Flyes', [
        E('Incline Smith Press', 4, '8-10', 120),
        E('Machine Shoulder Press', 3, '10-12', 90),
        E('Cable Flies', 3, '12-15', 60),
        E('Side Lateral Raise', 4, '15-20', 60),
        E('Overhead Cable Extension', 3, '10-12', 60),
      ]),
      D('Pull (volume)', 'Fri', 'Rows · Pulldown · Biceps', [
        E('Chest-Supported Row', 4, '8-12', 90),
        E('Lat Pulldown (wide)', 3, '10-12', 90),
        E('Cable Row (neutral)', 3, '10-12', 90),
        E('Reverse Machine Flyes', 3, '12-15', 60),
        E('Preacher Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Hammer Curl', 3, '10-12', 60, { ss: 'a' }),
      ]),
      D('Legs (volume)', 'Sat', 'Front squat · Split squat · Glutes', [
        E('Front Squat', 3, '8-10', 150),
        E('Bulgarian Split Squat', 3, '10-12', 90),
        E('Hip Thrust', 3, '10-12', 90),
        E('Leg Extensions', 3, '12-15', 60),
        E('Lying Leg Curl', 3, '12-15', 60),
        E('Seated Calf Raise', 4, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'arnold6',
    name: 'Classic 6-Day Antagonist Split',
    tagline: 'Chest & back · shoulders & arms · legs — twice a week, golden-era style.',
    description: 'The split made famous in the 1970s: chest is paired with back, shoulders with arms, and legs get their own day, each trained twice a week. Antagonist supersets save time and give a huge pump. High volume — for experienced lifters only.',
    level: 'advanced', levels: ['advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy'],
    daysPerWeek: 6, minutes: 80, weeks: 8, split: 'Chest-back / Shoulders-arms / Legs',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Double progression. Rest only as long as you need between supersets.',
    days: [
      D('Chest & Back', 'Mon', 'Bench + pull-up supersets', [
        E('Barbell Bench Press', 4, '6-10', 120, { ss: 'a' }),
        E('Weighted Pull-up', 4, '6-10', 120, { ss: 'a' }),
        E('Incline Dumbbell Press', 3, '8-12', 90, { ss: 'b' }),
        E('Bent Over Barbell Row', 3, '8-12', 90, { ss: 'b' }),
        E('Dumbbell Flyes', 3, '10-12', 60, { ss: 'c' }),
        E('Bent-Arm Dumbbell Pullover', 3, '10-12', 60, { ss: 'c' }),
      ]),
      D('Shoulders & Arms', 'Tue', 'Press · Laterals · Curls · Extensions', [
        E('Overhead Press', 4, '6-10', 120),
        E('Arnold Dumbbell Press', 3, '8-12', 90),
        E('Side Lateral Raise', 4, '12-15', 60),
        E('Barbell Curl', 3, '8-12', 60, { ss: 'a' }),
        E('Skull Crushers', 3, '8-12', 60, { ss: 'a' }),
        E('Incline Dumbbell Curl', 3, '10-12', 60, { ss: 'b' }),
        E('Cable Triceps Pushdown', 3, '10-12', 60, { ss: 'b' }),
      ]),
      D('Legs', 'Wed', 'Squat · Leg curl · Calves', [
        E('Back Squat', 4, '6-10', 180),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Leg Press', 3, '10-15', 120),
        E('Leg Extensions', 3, '12-15', 60, { ss: 'a' }),
        E('Lying Leg Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Standing Calf Raise', 5, '10-15', 45),
      ]),
      D('Chest & Back', 'Thu', 'Incline + row supersets', [
        E('Incline Smith Press', 4, '8-10', 120, { ss: 'a' }),
        E('Chest-Supported Row', 4, '8-10', 120, { ss: 'a' }),
        E('Flat DB Bench Press', 3, '8-12', 90, { ss: 'b' }),
        E('Lat Pulldown (wide)', 3, '10-12', 90, { ss: 'b' }),
        E('Cable Flies', 3, '12-15', 60, { ss: 'c' }),
        E('Cable Row (neutral)', 3, '10-12', 60, { ss: 'c' }),
      ]),
      D('Shoulders & Arms', 'Fri', 'Machine press · Rear delts · Arms', [
        E('Machine Shoulder Press', 4, '8-12', 90),
        E('Cable Lateral Raise', 4, '12-15', 60),
        E('Reverse Flyes', 3, '12-15', 60),
        E('Preacher Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Overhead Cable Extension', 3, '10-12', 60, { ss: 'a' }),
        E('Hammer Curl', 3, '10-12', 60, { ss: 'b' }),
        E('Dips - Triceps Version', 3, '8-15', 60, { ss: 'b' }),
      ]),
      D('Legs', 'Sat', 'Front squat · Lunges · Calves', [
        E('Front Squat', 4, '6-10', 150),
        E('Barbell Lunge', 3, '8-12', 90),
        E('Hip Thrust', 3, '8-12', 90),
        E('Seated Leg Curl', 3, '10-12', 60),
        E('Seated Calf Raise', 5, '12-15', 45),
        E('Hanging Leg Raise', 3, '10-15', 45),
      ]),
    ],
  },
  // ——— Training-max, tiered, RPE and mesocycle programs (run by progression.js) ———
  {
    id: 'wave-bv4',
    name: 'Four-Week Wave + Big Volume',
    tagline: 'A 5/3/1-style wave on the main lift, then 5×10 for size.',
    description: 'Four days, one main lift each. The main lift runs a four-week wave off your training max — 5s, 3s, then 5/3/1 with a "+" set where you take as many good reps as you can — and the fourth week is a deload. After it, 5 sets of 10 at 50–60% of the same training max builds muscle on the movement itself. Training maxes go up automatically at the end of each wave.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'strength', 'hypertrophy'],
    daysPerWeek: 4, minutes: 60, weeks: 4, split: 'Press / Deadlift / Bench / Squat',
    equipment: ['barbell', 'dumbbell', 'bench', 'pullup', 'cable'],
    progression: 'Percentages of a training max (90% of your 1RM). Week 3\'s "+" set decides the next wave: make the minimum reps and the TM goes up 5 lb (upper) or 10 lb (lower); fall short and it drops 10%. Week 4 is a deload.',
    cycle: CY.wave4,
    days: [
      D('Press', 'Mon', 'Overhead press · 5×10', [
        P('Overhead Press', 'w531', 180, { inc: 5 }),
        P('Overhead Press', 'bbb', 90),
        E('Chin-Up', 5, '8-10', 90),
        E('Face Pull', 3, '15-20', 60),
      ]),
      D('Deadlift', 'Tue', 'Deadlift · 5×10', [
        P('Conventional Deadlift', 'w531', 240, { inc: 10 }),
        P('Conventional Deadlift', 'bbb', 120),
        E('Hanging Leg Raise', 5, '10-15', 60),
      ]),
      D('Bench', 'Thu', 'Bench · 5×10', [
        P('Barbell Bench Press', 'w531', 180, { inc: 5 }),
        P('Barbell Bench Press', 'bbb', 90),
        E('One-Arm Dumbbell Row', 5, '10', 75),
      ]),
      D('Squat', 'Fri', 'Squat · 5×10', [
        P('Back Squat', 'w531', 240, { inc: 10 }),
        P('Back Squat', 'bbb', 120),
        E('Lying Leg Curl', 5, '10', 60),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
    ],
  },
  {
    id: 'wave-fsl3',
    name: 'Four-Week Wave, 3-Day',
    tagline: 'The same four-week wave in three sessions, with first-set-last back-downs.',
    description: 'Three full sessions a week. Each day has one main lift on a 5/3/1-style four-week wave followed by five sets of five at the day\'s first-set weight, plus a second press or pull. Less volume than the 5×10 version and easier to recover from, so it suits busy lifters.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding', 'general'],
    daysPerWeek: 3, minutes: 55, weeks: 4, split: 'Full body · 3 days',
    equipment: ['barbell', 'dumbbell', 'bench', 'pullup'],
    progression: 'Training-max percentages with a "+" set; TMs rise after week 3 if you make the minimum reps. Week 4 is a deload. Accessories use double progression.',
    cycle: CY.wave4,
    days: [
      D('Squat + Press', 'Mon', 'Squat wave · Press wave', [
        P('Back Squat', 'w531', 210, { inc: 10 }),
        P('Back Squat', 'fsl', 120),
        P('Overhead Press', 'w531', 150, { inc: 5 }),
        E('Chin-Up', 3, '6-10', 90),
      ]),
      D('Bench', 'Wed', 'Bench wave · Rows', [
        P('Barbell Bench Press', 'w531', 180, { inc: 5 }),
        P('Barbell Bench Press', 'fsl', 120),
        E('One-Arm Dumbbell Row', 4, '8-12', 75),
        E('Dumbbell Lunges', 3, '10-12', 75),
      ]),
      D('Deadlift', 'Fri', 'Deadlift wave · Press', [
        P('Conventional Deadlift', 'w531', 240, { inc: 10 }),
        P('Conventional Deadlift', 'fsl', 150),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Hanging Leg Raise', 3, '10-15', 60),
      ]),
    ],
  },
  {
    id: 'pyramid4',
    name: 'Daily Volume Pyramid, 4-Day',
    tagline: 'Nine-set pyramids on the big lifts, training maxes that rise every week.',
    description: 'An nSuns-style program. Each day opens with a nine-set tier-1 pyramid that climbs to a heavy single with a "+" and then works back down, followed by an eight-set tier-2 lift off a related training max. The reps on the heavy "+" set decide how much the training max rises next week, so strong days pay off immediately. High volume — for lifters who recover well.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 4, minutes: 80, weeks: 8, split: 'Bench / Squat / Bench / Deadlift',
    equipment: ['barbell', 'bench', 'cable', 'dumbbell'],
    progression: 'Run by the app every week: 0–1 reps on the 1+ set keeps the TM; 2–3 adds 5 lb; 4–5 adds 5 lb upper / 10 lb lower; 6+ adds 10 / 15 lb. Overhead press adds 5 lb when every rep is made.',
    days: [
      D('Bench + Press', 'Mon', 'T1 Bench volume · T2 Press', [
        P('Barbell Bench Press', 'pyrBench', 120),
        P('Overhead Press', 'pyrT2', 90, { tmRule: 'success', inc: 5 }),
        E('Lat Pulldown (wide)', 3, '8-12', 75),
        E('Face Pull', 3, '15-20', 60),
      ]),
      D('Squat + Sumo', 'Tue', 'T1 Squat · T2 Sumo deadlift', [
        P('Back Squat', 'pyrHeavy', 150),
        P('Sumo Deadlift', 'pyrT2', 120, { tmOf: 'Conventional Deadlift' }),
        E('Lying Leg Curl', 3, '10-12', 60),
        E('Hanging Leg Raise', 3, '10-15', 60),
      ]),
      D('Heavy Bench', 'Thu', 'T1 Bench 1+ · T2 Close-grip', [
        P('Barbell Bench Press', 'pyrHeavy', 150),
        P('Close-Grip Barbell Bench Press', 'pyrT2light', 90, { tmOf: 'Barbell Bench Press' }),
        E('Chest-Supported Row', 4, '8-12', 75),
        E('Cable Triceps Pushdown', 3, '10-15', 60),
      ]),
      D('Deadlift + Front Squat', 'Fri', 'T1 Deadlift · T2 Front squat', [
        P('Conventional Deadlift', 'pyrHeavy', 180),
        P('Front Squat', 'pyrT2front', 120, { tmOf: 'Back Squat' }),
        E('Bent Over Barbell Row', 3, '8-10', 90),
        E('Hammer Curl', 3, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'pyramid5',
    name: 'Daily Volume Pyramid, 5-Day',
    tagline: 'The pyramid program with a dedicated overhead-press day.',
    description: 'The five-day version adds an overhead-press day with its own heavy pyramid and an incline tier-2. Every main lift has a training max that moves weekly with your "+" sets. Expect long sessions and fast strength gains if your sleep and food keep up.',
    level: 'advanced', levels: ['advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 5, minutes: 85, weeks: 8, split: 'Bench / Squat / Press / Deadlift / Bench',
    equipment: ['barbell', 'bench', 'cable', 'dumbbell', 'pullup'],
    progression: 'Every main lift\'s training max rises weekly from its heavy "+" set (0–1 reps: hold; 2–3: +5 lb; 4–5: +5/10 lb; 6+: +10/15 lb). Tier-2 lifts ride those maxes.',
    days: [
      D('Bench volume', 'Mon', 'T1 Bench · T2 Press', [
        P('Barbell Bench Press', 'pyrBench', 120),
        P('Overhead Press', 'pyrT2', 90),
        E('Lat Pulldown (wide)', 3, '8-12', 75),
        E('Cable Lateral Raise', 3, '12-15', 45),
      ]),
      D('Squat', 'Tue', 'T1 Squat · T2 Sumo', [
        P('Back Squat', 'pyrHeavy', 150),
        P('Sumo Deadlift', 'pyrT2', 120, { tmOf: 'Conventional Deadlift' }),
        E('Lying Leg Curl', 3, '10-12', 60),
      ]),
      D('Press', 'Wed', 'T1 Press · T2 Incline', [
        P('Overhead Press', 'pyrHeavy', 120, { inc: 5 }),
        P('Barbell Incline Bench Press - Medium Grip', 'pyrT2light', 90, { tmOf: 'Barbell Bench Press' }),
        E('Weighted Pull-up', 3, '6-10', 90),
        E('Face Pull', 3, '15-20', 60),
      ]),
      D('Deadlift', 'Fri', 'T1 Deadlift · T2 Front squat', [
        P('Conventional Deadlift', 'pyrHeavy', 180),
        P('Front Squat', 'pyrT2front', 120, { tmOf: 'Back Squat' }),
        E('Chest-Supported Row', 3, '8-12', 75),
      ]),
      D('Heavy bench', 'Sat', 'T1 Bench 1+ · T2 Close-grip', [
        P('Barbell Bench Press', 'pyrHeavy', 150),
        P('Close-Grip Barbell Bench Press', 'pyrT2light', 90, { tmOf: 'Barbell Bench Press' }),
        E('One-Arm Dumbbell Row', 3, '8-12', 75),
        E('Hammer Curl', 3, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'vri3',
    name: 'Volume, Recovery, Intensity',
    tagline: 'Texas-method-style week: hard volume Monday, easy Wednesday, a new five Friday.',
    description: 'The classic bridge from novice to intermediate. Monday is five sets of five at a sub-maximal weight, Wednesday is a light recovery day, and Friday is one heavy set of five that should be a new best. When Friday\'s five is made, everything moves up next week.',
    level: 'intermediate', levels: ['beginner', 'intermediate'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 3, minutes: 60, weeks: 12, split: 'Volume / Recovery / Intensity',
    equipment: ['barbell', 'bench', 'pullup'],
    progression: 'Training-max based. Make Friday\'s set of five and the TM goes up 5 lb (2.5 lb pressing), so Monday and Wednesday follow. Miss it twice and the TM resets 10%. Press, deadlift and power clean run linear progression.',
    days: [
      D('Volume', 'Mon', 'Squat 5×5 · Bench 5×5 · Deadlift', [
        P('Back Squat', 'txVolume', 210),
        P('Barbell Bench Press', 'txVolume', 180),
        L('Conventional Deadlift', 1, 5, 240, { inc: 10 }),
      ]),
      D('Recovery', 'Wed', 'Light squat · Press · Chins', [
        P('Back Squat', 'txRecovery', 150),
        L('Overhead Press', 3, 5, 150, { inc: 2.5 }),
        E('Chin-Up', 3, '6-10', 90),
      ]),
      D('Intensity', 'Fri', 'Squat 1×5 · Bench 1×5 · Clean', [
        P('Back Squat', 'txIntensity', 240, { inc: 5 }),
        P('Barbell Bench Press', 'txIntensity', 210, { inc: 2.5 }),
        L('Power Clean', 5, 3, 120, { inc: 5 }),
      ]),
    ],
  },
  {
    id: 'novice-3x5',
    name: 'Novice Barbell 3×5',
    tagline: 'Starting-Strength-style: alternate two short workouts, add weight every time.',
    description: 'Two barbell workouts alternated three times a week (A-B-A, then B-A-B). Squat every session, alternate bench and overhead press, deadlift and power clean. Three sets of five at one weight. It is the fastest way a new lifter gets strong — weight goes up every session until it can\'t.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'strength', goals: ['strength', 'general'],
    daysPerWeek: 3, minutes: 45, weeks: 12, split: 'A / B alternating',
    equipment: ['barbell', 'bench'],
    progression: 'Linear, run by the app: make every rep and the lift goes up next session (squat 5 lb, deadlift 10 lb, presses 2.5–5 lb). Three misses in a row → 10% reset.',
    days: [
      D('Workout A', 'Mon', 'Squat · Bench · Deadlift', [
        L('Back Squat', 3, 5, 180),
        L('Barbell Bench Press', 3, 5, 180),
        L('Conventional Deadlift', 1, 5, 240, { inc: 10 }),
      ]),
      D('Workout B', 'Wed', 'Squat · Press · Power clean', [
        L('Back Squat', 3, 5, 180),
        L('Overhead Press', 3, 5, 180, { inc: 2.5 }),
        L('Power Clean', 5, 3, 120),
      ]),
    ],
  },
  {
    id: 'block6',
    name: '6-Week Strength Block',
    tagline: 'Candito-style block: conditioning to heavy singles to a test week.',
    description: 'Six weeks that move from higher-rep conditioning work through heavy triples and doubles to a test week, on an upper/lower split. Squat, bench and deadlift run the block from your training max; accessories build the muscle behind them. Your training max is reset from the test week before the next block.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 4, minutes: 70, weeks: 6, split: 'Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'pullup', 'cable'],
    progression: 'Weekly percentages of your training max. Week 6 tests a heavy single; the app sets the next block\'s TM to 90% of the new estimated max.',
    cycle: CY.block6,
    days: [
      D('Lower — Squat', 'Mon', 'Squat block · Hinge · Legs', [
        P('Back Squat', 'block6', 210),
        E('Romanian Deadlift', 3, '6-8', 150),
        E('Leg Press', 3, '8-12', 120),
        E('Standing Calf Raise', 3, '8-12', 60),
      ]),
      D('Upper — Bench', 'Tue', 'Bench block · Rows · Press', [
        P('Barbell Bench Press', 'block6', 180),
        E('Bent Over Barbell Row', 4, '6-8', 120),
        E('Overhead Press', 3, '6-8', 120),
        E('Hammer Curl', 3, '10-12', 60),
      ]),
      D('Lower — Deadlift', 'Thu', 'Deadlift block · Front squat', [
        P('Conventional Deadlift', 'block6', 240),
        E('Front Squat', 3, '5-8', 150),
        E('Lying Leg Curl', 3, '8-12', 75),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
      D('Upper — Volume', 'Fri', 'Close-grip · Pull-ups · Shoulders', [
        E('Close-Grip Barbell Bench Press', 4, '6-8', 120),
        E('Weighted Pull-up', 4, '5-8', 120),
        E('Cable Lateral Raise', 3, '12-15', 45),
        E('Cable Triceps Pushdown', 3, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'peak12',
    name: '12-Week Powerlifting Peak',
    tagline: 'Volume, strength, peak, taper — arrive at test day at your best.',
    description: 'A full meet-prep block for squat, bench and deadlift. Three weeks of volume, a deload, three strength weeks, a deload, two peaking weeks with heavy singles, a taper, then test day. Accessories shrink as the block gets heavier.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength'],
    daysPerWeek: 4, minutes: 75, weeks: 12, split: 'Squat / Bench / Deadlift / Bench',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'pullup'],
    progression: 'Week-by-week percentages of your training max with scheduled deloads in weeks 4, 8 and 11. Test day sets your next training max from the heaviest single.',
    cycle: CY.peak12,
    days: [
      D('Squat', 'Mon', 'Squat · Hinge accessory', [
        P('Back Squat', 'peak12', 240),
        E('Romanian Deadlift', 3, '6-8', 150),
        E('Bulgarian Split Squat', 2, '8-10', 90),
      ]),
      D('Bench', 'Tue', 'Bench · Upper back', [
        P('Barbell Bench Press', 'peak12', 210),
        E('Chest-Supported Row', 4, '8-10', 90),
        E('Overhead Press', 3, '6-8', 120),
      ]),
      D('Deadlift', 'Thu', 'Deadlift · Core', [
        P('Conventional Deadlift', 'peak12', 270),
        E('Front Squat', 3, '5-6', 150),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
      D('Bench volume', 'Sat', 'Bench volume · Arms', [
        P('Barbell Bench Press', 'dupMedium', 150),
        E('Weighted Pull-up', 3, '5-8', 120),
        E('Cable Triceps Pushdown', 3, '10-12', 60),
        E('Face Pull', 3, '15-20', 60),
      ]),
    ],
  },
  {
    id: 'rtf4',
    name: 'Rep-Out Hypertrophy, 4-Day',
    tagline: 'Percent-based size training: four sets, the last one to your rep-out target.',
    description: 'An RTF/SBS-style program. Each main lift is three sets at a percentage of your training max plus a fourth set taken to the rep-out target, about one rep short of failure. Beat the target and your training max rises; fall short and it eases off — the program adjusts itself every week. Week 7 is a deload.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'powerbuilding'],
    daysPerWeek: 4, minutes: 65, weeks: 7, split: 'Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Run by the app every session: each rep above the rep-out target raises the training max 0.5% (up to 3%); one short lowers it 2%, two or more short lowers it 5%.',
    cycle: CY.rtf7,
    days: [
      D('Lower A', 'Mon', 'Squat rep-out · Legs', [
        P('Back Squat', 'rtf', 180),
        E('Romanian Deadlift', 3, '8-10', 120),
        E('Leg Extensions', 3, '10-15', 60),
        E('Standing Calf Raise', 4, '10-15', 60),
      ]),
      D('Upper A', 'Tue', 'Bench rep-out · Back', [
        P('Barbell Bench Press', 'rtf', 150),
        E('Lat Pulldown (wide)', 4, '8-12', 75),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Cable Lateral Raise', 3, '12-20', 45),
      ]),
      D('Lower B', 'Thu', 'Deadlift rep-out · Legs', [
        P('Conventional Deadlift', 'rtf', 180),
        E('Leg Press', 3, '10-15', 90),
        E('Seated Leg Curl', 3, '10-15', 60),
        E('Hanging Leg Raise', 3, '10-15', 60),
      ]),
      D('Upper B', 'Fri', 'Press rep-out · Arms', [
        P('Overhead Press', 'rtf', 120),
        E('Chest-Supported Row', 4, '8-12', 75),
        E('EZ-Bar Curl', 3, '8-12', 60),
        E('Overhead Cable Extension', 3, '10-15', 60),
      ]),
    ],
  },
  {
    id: 'meso-ul',
    name: 'Hypertrophy Meso: Upper/Lower',
    tagline: 'RP-style five-week mesocycle: effort ramps up, sets climb, then a deload.',
    description: 'A four-week accumulation block and a deload. Week 1 stops three reps short of failure; every week after that gets one rep closer and adds a set per exercise, until week 4 goes to failure. Then a deload with half the sets. If your reps drop, the app holds the set count instead of piling on fatigue.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy'],
    daysPerWeek: 4, minutes: 65, weeks: 5, split: 'Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Run by the app: RIR 3 → 2 → 1 → 0, +1 set a week per exercise between its minimum and maximum, deload week 5. Load uses double progression within the rep range.',
    cycle: CY.meso5,
    days: [
      D('Upper A', 'Mon', 'Chest · Back · Arms', [
        M('Barbell Bench Press', 2, 4, '6-10', 150),
        M('Chest-Supported Row', 2, 4, '8-12', 120),
        M('Lat Pulldown (wide)', 2, 4, '10-12', 90),
        M('Cable Lateral Raise', 2, 5, '12-20', 45),
        M('EZ-Bar Curl', 2, 4, '10-12', 60),
        M('Cable Triceps Pushdown', 2, 4, '10-15', 60),
      ]),
      D('Lower A', 'Tue', 'Quads · Hamstrings · Calves', [
        M('Back Squat', 2, 4, '6-10', 180),
        M('Romanian Deadlift', 2, 4, '8-10', 150),
        M('Leg Extensions', 2, 4, '10-15', 60),
        M('Seated Leg Curl', 2, 4, '10-15', 60),
        M('Standing Calf Raise', 2, 5, '10-15', 60),
      ]),
      D('Upper B', 'Thu', 'Shoulders · Back · Chest', [
        M('Standing DB Shoulder Press', 2, 4, '8-12', 120),
        M('Cable Row (neutral)', 2, 4, '10-12', 90),
        M('Incline Dumbbell Press', 2, 4, '8-12', 120),
        M('Weighted Pull-up', 2, 4, '6-10', 120),
        M('Hammer Curl', 2, 4, '10-12', 60),
        M('Overhead Cable Extension', 2, 4, '10-15', 60),
      ]),
      D('Lower B', 'Fri', 'Hinge · Unilateral · Glutes', [
        M('Conventional Deadlift', 2, 3, '5-8', 210),
        M('Hack Squat', 2, 4, '8-12', 120),
        M('Lying Leg Curl', 2, 4, '10-12', 60),
        M('Hip Thrust', 2, 4, '8-12', 90),
        M('Seated Calf Raise', 2, 5, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'meso-ppl',
    name: 'Hypertrophy Meso: Push/Pull/Legs',
    tagline: 'Six days, every muscle twice, sets and effort ramping over five weeks.',
    description: 'The same five-week mesocycle logic on a push/pull/legs split run twice a week. Built for advanced lifters chasing size: each exercise starts at its minimum effective sets, climbs a set a week toward its maximum, finishes week 4 at failure, then deloads.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy'],
    daysPerWeek: 6, minutes: 45, weeks: 5, split: 'Push / Pull / Legs ×2',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Run by the app: RIR 3 → 0 over four weeks, +1 set a week up to each exercise\'s maximum, deload in week 5. Missed reps hold the set count.',
    cycle: CY.meso5,
    days: [
      D('Push A', 'Mon', 'Chest · Shoulders · Triceps', [
        M('Barbell Bench Press', 2, 4, '6-10', 150),
        M('Machine Shoulder Press', 2, 3, '8-12', 90),
        M('Cable Flies', 2, 4, '12-15', 60),
        M('Cable Lateral Raise', 2, 5, '12-20', 45),
        M('Cable Triceps Pushdown', 2, 4, '10-15', 60),
      ]),
      D('Pull A', 'Tue', 'Back · Rear delts · Biceps', [
        M('Weighted Pull-up', 2, 4, '6-10', 120),
        M('Chest-Supported Row', 2, 4, '8-12', 90),
        M('Face Pull', 2, 4, '15-20', 45),
        M('EZ-Bar Curl', 2, 4, '8-12', 60),
      ]),
      D('Legs A', 'Wed', 'Quads · Hamstrings', [
        M('Back Squat', 2, 4, '6-10', 180),
        M('Romanian Deadlift', 2, 3, '8-10', 150),
        M('Leg Extensions', 2, 4, '10-15', 60),
        M('Standing Calf Raise', 2, 5, '10-15', 45),
      ]),
      D('Push B', 'Thu', 'Incline · Shoulders · Triceps', [
        M('Incline Dumbbell Press', 2, 4, '8-12', 120),
        M('Standing DB Shoulder Press', 2, 3, '8-12', 90),
        M('Pec Deck', 2, 4, '12-15', 60),
        M('Overhead Cable Extension', 2, 4, '10-15', 60),
      ]),
      D('Pull B', 'Fri', 'Lats · Upper back · Biceps', [
        M('Lat Pulldown (wide)', 2, 4, '10-12', 90),
        M('Cable Row (neutral)', 2, 4, '10-12', 90),
        M('Cable Rear Delt Fly', 2, 4, '15-20', 45),
        M('Preacher Curl', 2, 4, '10-12', 60),
      ]),
      D('Legs B', 'Sat', 'Hinge · Unilateral · Glutes', [
        M('Hack Squat', 2, 4, '8-12', 120),
        M('Seated Leg Curl', 2, 4, '10-15', 60),
        M('Bulgarian Split Squat', 2, 3, '8-12', 90),
        M('Seated Calf Raise', 2, 5, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'phat5',
    name: 'Power + Hypertrophy 5-Day',
    tagline: 'PHAT-style: two heavy power days, three volume days.',
    description: 'Two power days (upper and lower) built on heavy top sets at RPE 8 with back-offs, and three hypertrophy days split by body part with moderate reps and short rests. Strength and size in one week — for intermediates with time to train five days.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'hypertrophy', 'strength'],
    daysPerWeek: 5, minutes: 70, weeks: 8, split: 'Power ×2 · Hypertrophy ×3',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Power lifts: top set at RPE 8, back-offs 10% lighter from today\'s e1RM, re-estimated each session. Hypertrophy days: double progression.',
    days: [
      D('Upper power', 'Mon', 'Bench · Row · Pull-up', [
        R('Barbell Bench Press', 3, 2, 5, 180),
        R('Bent Over Barbell Row', 5, 2, 5, 150),
        E('Weighted Pull-up', 3, '6-10', 120),
        E('Standing DB Shoulder Press', 2, '6-10', 90),
        E('EZ-Bar Curl', 2, '6-10', 60),
      ]),
      D('Lower power', 'Tue', 'Squat · Deadlift', [
        R('Back Squat', 3, 2, 5, 210),
        R('Conventional Deadlift', 3, 1, 5, 240),
        E('Leg Press', 2, '8-12', 120),
        E('Seated Leg Curl', 2, '8-12', 75),
        E('Standing Calf Raise', 3, '8-12', 60),
      ]),
      D('Back + shoulders', 'Thu', 'Hypertrophy', [
        E('Chest-Supported Row', 3, '8-12', 75),
        E('Lat Pulldown (wide)', 3, '10-15', 75),
        E('Cable Row (neutral)', 2, '12-15', 60),
        E('Machine Shoulder Press', 3, '8-12', 75),
        E('Cable Lateral Raise', 3, '12-20', 45),
        E('Face Pull', 2, '15-20', 45),
      ]),
      D('Lower hypertrophy', 'Fri', 'Quads · Hamstrings · Calves', [
        E('Hack Squat', 3, '8-12', 120),
        E('Romanian Deadlift', 3, '8-12', 120),
        E('Leg Extensions', 3, '12-15', 60),
        E('Lying Leg Curl', 3, '12-15', 60),
        E('Seated Calf Raise', 4, '12-15', 45),
      ]),
      D('Chest + arms', 'Sat', 'Hypertrophy', [
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Flat DB Bench Press', 3, '8-12', 90),
        E('Cable Flies', 2, '12-15', 60),
        E('Preacher Curl', 3, '8-12', 60),
        E('Overhead Cable Extension', 3, '10-15', 60),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'glute-meso',
    name: 'Glute Builder Meso',
    tagline: 'A five-week glute-focused block for women — sets climb, then a deload.',
    description: 'Lower-body days built around hip thrusts, Romanian deadlifts, split squats and abduction, with two lighter upper days to keep the rest of you balanced. Glute work runs the mesocycle: start three reps from failure, add a set a week, finish week 4 near failure, then deload.',
    level: 'beginner', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 4, minutes: 55, weeks: 5, split: 'Glutes ×2 · Upper ×2', specialty: true,
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Glute and leg work: RIR 3 → 0 with +1 set a week (to each exercise\'s maximum), deload week 5. Upper days: double progression.',
    cycle: CY.meso5,
    days: [
      D('Glutes A', 'Mon', 'Thrust · RDL · Abduction', [
        M('Hip Thrust', 3, 5, '8-12', 120),
        M('Romanian Deadlift', 2, 4, '8-10', 120),
        M('Bulgarian Split Squat', 2, 4, '10-12', 90),
        M('Thigh Abductor', 2, 5, '15-20', 45),
      ]),
      D('Upper A', 'Tue', 'Back · Shoulders', [
        E('Lat Pulldown (wide)', 3, '10-12', 75),
        E('Seated Dumbbell Press', 3, '10-12', 75),
        E('Cable Row (neutral)', 3, '10-12', 75),
        E('Cable Lateral Raise', 3, '12-20', 45),
      ]),
      D('Glutes B', 'Thu', 'Squat · Bridge · Kickback', [
        M('Back Squat', 2, 4, '8-10', 150),
        M('Barbell Glute Bridge', 2, 4, '10-15', 90),
        M('Seated Leg Curl', 2, 4, '10-15', 60),
        M('One-Legged Cable Kickback', 2, 4, '12-15', 45),
      ]),
      D('Upper B', 'Sat', 'Chest · Arms · Core', [
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('One-Arm Dumbbell Row', 3, '10-12', 75),
        E('Hammer Curl', 2, '10-12', 60),
        E('Cable Triceps Pushdown', 2, '10-15', 60),
        E('Cable Crunch', 3, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'dup3',
    name: 'Undulating Full Body 3-Day',
    tagline: 'Heavy, medium and light days on rotation — the heavy day sets the pace.',
    description: 'Daily undulating periodization: squat and bench are trained heavy, medium and light across the week (deadlift once, heavy). Each heavy day ends in a rep-out set, and how many reps you get adjusts that lift\'s training max for the next week.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding', 'general'],
    daysPerWeek: 3, minutes: 65, weeks: 8, split: 'Full body · H / M / L',
    equipment: ['barbell', 'bench', 'dumbbell', 'pullup'],
    progression: 'Percent of training max. The heavy day\'s last set is a rep-out: beat the target and the TM rises up to 3%; fall short and it eases off.',
    days: [
      D('Day 1', 'Mon', 'Squat heavy · Bench medium', [
        P('Back Squat', 'dupHeavy', 210),
        P('Barbell Bench Press', 'dupMedium', 150),
        E('Chin-Up', 3, '6-10', 90),
        E('Hanging Leg Raise', 3, '10-15', 60),
      ]),
      D('Day 2', 'Wed', 'Deadlift heavy · Bench light · Press', [
        P('Conventional Deadlift', 'dupHeavy', 240),
        P('Barbell Bench Press', 'dupLight', 120),
        E('Overhead Press', 3, '6-8', 120),
        E('One-Arm Dumbbell Row', 3, '8-12', 75),
      ]),
      D('Day 3', 'Fri', 'Bench heavy · Squat medium', [
        P('Barbell Bench Press', 'dupHeavy', 180),
        P('Back Squat', 'dupMedium', 180),
        E('Romanian Deadlift', 3, '8-10', 120),
        E('Face Pull', 3, '15-20', 60),
      ]),
    ],
  },
  {
    id: 'bench6',
    name: 'Bench Specialization, 6 Weeks',
    tagline: 'Three bench sessions a week, ramping to a test — the rest on maintenance.',
    description: 'For lifters whose bench is lagging. A heavy day ramps from fives to singles and a test, a volume day builds work capacity, and a close-grip day strengthens lockout. Squat and deadlift drop to maintenance volume so recovery goes to the bench.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding'],
    daysPerWeek: 3, minutes: 60, weeks: 6, split: 'Bench ×3 · Full body', specialty: true,
    equipment: ['barbell', 'bench', 'dumbbell', 'cable', 'pullup'],
    progression: 'Bench runs a six-week percent ramp off your training max; week 6 tests a heavy single and resets the TM. Everything else uses double progression.',
    cycle: CY.bench6,
    days: [
      D('Heavy bench', 'Mon', 'Bench heavy · Squat', [
        P('Barbell Bench Press', 'benchHeavy', 210),
        E('Back Squat', 3, '5-6', 180),
        E('Chest-Supported Row', 4, '8-10', 90),
      ]),
      D('Volume bench', 'Wed', 'Bench volume · Pull-ups', [
        P('Barbell Bench Press', 'benchVolume', 150),
        E('Weighted Pull-up', 4, '5-8', 120),
        E('Standing DB Shoulder Press', 3, '8-12', 90),
        E('Cable Triceps Pushdown', 3, '10-15', 60),
      ]),
      D('Close-grip', 'Fri', 'Close-grip · Deadlift', [
        E('Close-Grip Barbell Bench Press', 4, '5-8', 150),
        E('Conventional Deadlift', 2, '3-5', 210),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Face Pull', 3, '15-20', 60),
      ]),
    ],
  },
  {
    id: 'rpe-fb3',
    name: 'Full Body Top Sets 3-Day',
    tagline: 'Autoregulated: one top set at a target RPE, back-offs from today\'s strength.',
    description: 'Each session works up to one top set on two big lifts at a target effort, then back-off sets 10% lighter than what today\'s top set says you can handle. Bad day? The back-offs come down. Great day? They go up. A four-week wave steps the RPE from 7 to 9, then deloads.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'strength', 'hypertrophy'],
    daysPerWeek: 3, minutes: 60, weeks: 4, split: 'Full body',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'pullup'],
    progression: 'Run by the app: log RPE on the top set and the back-offs are recalculated live. Your e1RM updates after every session; next week\'s top set follows it. RPE 7 → 8 → 9, deload in week 4.',
    cycle: CY.rpe4,
    days: [
      D('Day 1', 'Mon', 'Squat · Bench', [
        R('Back Squat', 5, 3, 5, 210, { rpe: [7, 8, 9, 6] }),
        R('Barbell Bench Press', 5, 3, 5, 180, { rpe: [7, 8, 9, 6] }),
        E('Chest-Supported Row', 3, '8-12', 75),
        E('Cable Lateral Raise', 3, '12-20', 45),
      ]),
      D('Day 2', 'Wed', 'Deadlift · Press', [
        R('Conventional Deadlift', 3, 2, 5, 240, { rpe: [7, 8, 9, 6] }),
        R('Overhead Press', 5, 3, 6, 150, { rpe: [7, 8, 9, 6] }),
        E('Lat Pulldown (wide)', 3, '8-12', 75),
        E('Hammer Curl', 3, '10-12', 60),
      ]),
      D('Day 3', 'Fri', 'Bench · Front squat', [
        R('Barbell Bench Press', 3, 3, 6, 180, { rpe: [7, 8, 9, 6] }),
        R('Front Squat', 5, 2, 6, 180, { rpe: [7, 8, 9, 6] }),
        E('One-Arm Dumbbell Row', 3, '8-12', 75),
        E('Cable Triceps Pushdown', 3, '10-15', 60),
      ]),
    ],
  },
  // ——— Library wave 3: breadth across goals, equipment and schedules ———
  {
    id: 'lp-amrap',
    name: 'Novice Linear with + Sets',
    tagline: 'Three sets of five with a last set to the max: fast, autoregulated novice gains.',
    description: 'Two alternating full-body workouts, three days a week. Every main lift is 2×5 plus a last set of five or more, as many good reps as you can. Make the reps and the weight goes up next time; hit 10 or more on the last set and it goes up twice as much. Miss and it drops 10% straight away, so you never grind a stall. Chin-ups and rows balance all the pressing.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'strength', goals: ['strength', 'general', 'powerbuilding'],
    daysPerWeek: 3, minutes: 50, weeks: 12, split: 'A / B alternating',
    equipment: ['barbell', 'bench', 'pullup'],
    progression: 'Linear, run by the app: make 5 on every set and the lift goes up next session (squat 5 lb, deadlift 10 lb, presses and row 2.5 lb). 10+ reps on the last set doubles the jump. Miss the reps once and the weight drops 10%.',
    days: [
      D('Workout A', 'Mon', 'Press · Chin-up · Squat', [
        L('Overhead Press', 3, 5, 150, { inc: 2.5, failLimit: 1, amrapLast: true, doubleAt: 10 }),
        E('Chin-Up', 3, '5-8', 120),
        L('Back Squat', 3, 5, 180, { failLimit: 1, amrapLast: true, doubleAt: 10 }),
      ]),
      D('Workout B', 'Wed', 'Bench · Row · Deadlift', [
        L('Barbell Bench Press', 3, 5, 150, { inc: 2.5, failLimit: 1, amrapLast: true, doubleAt: 10 }),
        L('Bent Over Barbell Row', 3, 5, 120, { inc: 2.5, failLimit: 1, amrapLast: true, doubleAt: 10 }),
        L('Conventional Deadlift', 1, 5, 210, { inc: 10, failLimit: 1, amrapLast: true, doubleAt: 10 }),
      ]),
      D('Workout A', 'Fri', 'Press · Chin-up · Squat', [
        L('Overhead Press', 3, 5, 150, { inc: 2.5, failLimit: 1, amrapLast: true, doubleAt: 10 }),
        E('Chin-Up', 3, '5-8', 120),
        L('Back Squat', 3, 5, 180, { failLimit: 1, amrapLast: true, doubleAt: 10 }),
      ]),
    ],
  },
  {
    id: 'ul4-novice',
    name: 'Beginner Upper/Lower Linear',
    tagline: 'Four days, linear on the big lifts, rep ranges on the rest.',
    description: 'A first upper/lower split. Each day starts with one barbell lift at 3×5 that goes up every session, then moves to dumbbell, cable and machine work in moderate rep ranges for muscle. More weekly volume than a three-day novice plan, without the complexity of an intermediate program.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general', 'strength', 'hypertrophy', 'powerbuilding'],
    daysPerWeek: 4, minutes: 60, weeks: 12, split: 'Upper / Lower',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Main lifts are linear: make all 3×5 and they go up next session (deadlift 10 lb, press 2.5 lb). Three misses in a row → 10% reset. Accessories use double progression.',
    days: [
      D('Upper A', 'Mon', 'Bench · Row · Shoulders', [
        L('Barbell Bench Press', 3, 5, 180),
        L('Bent Over Barbell Row', 3, 5, 150),
        E('Standing DB Shoulder Press', 3, '8-12', 90),
        E('Lat Pulldown (wide)', 3, '8-12', 90),
        E('EZ-Bar Curl', 2, '10-15', 60, { ss: 'a' }),
        E('Cable Triceps Pushdown', 2, '10-15', 60, { ss: 'a' }),
      ]),
      D('Lower A', 'Tue', 'Squat · RDL · Legs', [
        L('Back Squat', 3, 5, 180),
        E('Romanian Deadlift', 3, '8-10', 120),
        E('Leg Press', 2, '10-12', 120),
        E('Seated Leg Curl', 2, '10-12', 75),
        E('Standing Calf Raise', 3, '10-15', 60),
      ]),
      D('Upper B', 'Thu', 'Press · Chin-up · Incline', [
        L('Overhead Press', 3, 5, 150, { inc: 2.5 }),
        E('Chin-Up', 3, '5-10', 120),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Cable Row (neutral)', 3, '8-12', 90),
        E('Side Lateral Raise', 3, '12-15', 60),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
      D('Lower B', 'Fri', 'Deadlift · Split squat · Glutes', [
        L('Conventional Deadlift', 1, 5, 240, { inc: 10 }),
        E('Bulgarian Split Squat', 3, '8-10', 90),
        E('Hip Thrust', 3, '8-12', 90),
        E('Lying Leg Curl', 2, '10-12', 75),
        E('Hanging Leg Raise', 3, '8-12', 60),
      ]),
    ],
  },
  {
    id: 'ppl3',
    name: 'Push/Pull/Legs 3-Day',
    tagline: 'The classic split, once a week each: simple and easy to recover from.',
    description: 'Push, pull and legs on three non-consecutive days. Each session leads with a heavy compound and follows with machine, cable and dumbbell work. Every muscle gets one hard session a week, which is enough for beginners and a good maintenance plan for busy intermediates.',
    level: 'beginner', levels: ['beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 3, minutes: 60, weeks: 10, split: 'Push / Pull / Legs',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Double progression: reach the top of the range on every set, then add weight. Keep 1–2 reps in reserve on compounds.',
    days: [
      D('Push', 'Mon', 'Chest · Shoulders · Triceps', [
        E('Barbell Bench Press', 3, '6-8', 150),
        E('Seated Dumbbell Press', 3, '8-10', 120),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Cable Lateral Raise', 3, '12-15', 60),
        E('Cable Triceps Pushdown', 3, '10-12', 60),
        E('Overhead Cable Extension', 2, '10-12', 60),
      ]),
      D('Pull', 'Wed', 'Back · Rear delts · Biceps', [
        E('Bent Over Barbell Row', 3, '6-8', 150),
        E('Lat Pulldown (wide)', 3, '8-12', 90),
        E('Cable Row (neutral)', 3, '10-12', 90),
        E('Face Pull', 3, '15-20', 60),
        E('EZ-Bar Curl', 3, '8-12', 60),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
      D('Legs', 'Fri', 'Squat · Hinge · Calves', [
        E('Back Squat', 3, '5-8', 180),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Leg Press', 3, '10-12', 120),
        E('Seated Leg Curl', 3, '10-12', 75),
        E('Standing Calf Raise', 4, '10-15', 60),
      ]),
    ],
  },
  {
    id: 'ppl6-linear',
    name: 'Linear Push/Pull/Legs 6-Day',
    tagline: 'Six days, a + set on the big lift each day, lots of accessory volume.',
    description: 'Each day opens with one barbell lift for 5×5 (or 1×5 on deadlift) with the last set taken as far as you can, then a block of hypertrophy accessories with short rest. Bench and overhead press alternate as the lead lift on push days; deadlift and rows alternate on pull days. A big jump in weekly volume for a late beginner who can train six days.',
    level: 'intermediate', levels: ['beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'powerbuilding', 'strength'],
    daysPerWeek: 6, minutes: 70, weeks: 12, split: 'Push / Pull / Legs ×2',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Lead lifts are linear with a + last set: make every rep and add weight next session (deadlift 10 lb, overhead press 2.5 lb). Three misses → 10% reset. Accessories: double progression.',
    days: [
      D('Pull A', 'Mon', 'Deadlift · Back · Biceps', [
        L('Conventional Deadlift', 1, 5, 210, { inc: 10, amrapLast: true }),
        E('Lat Pulldown (wide)', 3, '8-12', 90),
        E('Cable Row (neutral)', 3, '8-12', 90),
        E('Face Pull', 5, '15-20', 45),
        E('Hammer Curl', 4, '8-12', 60),
        E('Dumbbell Bicep Curl', 4, '8-12', 60),
      ]),
      D('Push A', 'Tue', 'Bench · Shoulders · Triceps', [
        L('Barbell Bench Press', 5, 5, 180, { amrapLast: true }),
        E('Overhead Press', 3, '8-12', 120),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Cable Triceps Pushdown', 3, '8-12', 60, { ss: 'a' }),
        E('Side Lateral Raise', 3, '15-20', 60, { ss: 'a' }),
        E('Overhead Cable Extension', 3, '8-12', 60, { ss: 'b' }),
        E('Cable Lateral Raise', 3, '15-20', 60, { ss: 'b' }),
      ]),
      D('Legs A', 'Wed', 'Squat · Hinge · Calves', [
        L('Back Squat', 3, 5, 180, { amrapLast: true }),
        E('Romanian Deadlift', 3, '8-12', 120),
        E('Leg Press', 3, '8-12', 120),
        E('Seated Leg Curl', 3, '8-12', 75),
        E('Standing Calf Raise', 5, '8-12', 60),
      ]),
      D('Pull B', 'Thu', 'Row · Back · Biceps', [
        L('Bent Over Barbell Row', 5, 5, 150, { amrapLast: true }),
        E('Lat Pulldown (wide)', 3, '8-12', 90),
        E('Cable Row (neutral)', 3, '8-12', 90),
        E('Face Pull', 5, '15-20', 45),
        E('Hammer Curl', 4, '8-12', 60),
        E('Dumbbell Bicep Curl', 4, '8-12', 60),
      ]),
      D('Push B', 'Fri', 'Press · Chest · Triceps', [
        L('Overhead Press', 5, 5, 180, { inc: 2.5, amrapLast: true }),
        E('Barbell Bench Press', 3, '8-12', 120),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Cable Triceps Pushdown', 3, '8-12', 60, { ss: 'a' }),
        E('Side Lateral Raise', 3, '15-20', 60, { ss: 'a' }),
        E('Overhead Cable Extension', 3, '8-12', 60, { ss: 'b' }),
        E('Cable Lateral Raise', 3, '15-20', 60, { ss: 'b' }),
      ]),
      D('Legs B', 'Sat', 'Squat · Hinge · Calves', [
        L('Back Squat', 3, 5, 180, { amrapLast: true }),
        E('Romanian Deadlift', 3, '8-12', 120),
        E('Leg Press', 3, '8-12', 120),
        E('Lying Leg Curl', 3, '8-12', 75),
        E('Seated Calf Raise', 5, '8-12', 60),
      ]),
    ],
  },
  {
    id: 'ulppl5',
    name: 'Upper/Lower + Push/Pull/Legs 5-Day',
    tagline: 'Two strength-leaning days, three pump days: every muscle twice a week.',
    description: 'Monday and Tuesday are heavier upper and lower sessions built on 5–8 rep compounds. Thursday to Saturday run push, pull and legs with more isolation work and higher reps. Five sessions give every muscle two hits a week and plenty of volume without six-day weeks.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'powerbuilding'],
    daysPerWeek: 5, minutes: 60, weeks: 10, split: 'Upper / Lower / Push / Pull / Legs',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Double progression: top of the range on every set, then add weight. Compounds 1–2 reps in reserve, isolation to 0–1. Deload every 6–8 weeks.',
    days: [
      D('Upper', 'Mon', 'Bench · Row · Press · Pull-up', [
        E('Barbell Bench Press', 3, '5-8', 180),
        E('Bent Over Barbell Row', 3, '6-8', 150),
        E('Overhead Press', 3, '6-8', 150),
        E('Weighted Pull-up', 3, '6-8', 150),
        E('EZ-Bar Curl', 2, '8-10', 60, { ss: 'a' }),
        E('Skull Crushers', 2, '8-10', 60, { ss: 'a' }),
      ]),
      D('Lower', 'Tue', 'Squat · RDL · Legs', [
        E('Back Squat', 3, '5-8', 210),
        E('Romanian Deadlift', 3, '6-8', 180),
        E('Leg Press', 3, '10-12', 120),
        E('Lying Leg Curl', 3, '10-12', 75),
        E('Standing Calf Raise', 3, '8-12', 60),
      ]),
      D('Push', 'Thu', 'Incline · Shoulders · Triceps', [
        E('Incline Dumbbell Press', 3, '8-12', 120),
        E('Machine Shoulder Press', 3, '10-12', 90),
        E('Cable Flies', 3, '12-15', 60),
        E('Cable Lateral Raise', 4, '12-20', 45),
        E('Overhead Cable Extension', 3, '10-15', 60),
      ]),
      D('Pull', 'Fri', 'Lats · Upper back · Biceps', [
        E('Lat Pulldown (wide)', 3, '8-12', 90),
        E('Chest-Supported Row', 3, '8-12', 90),
        E('Face Pull', 3, '15-20', 45),
        E('Cable Rear Delt Fly', 2, '15-20', 45),
        E('Incline Dumbbell Curl', 3, '10-12', 60),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
      D('Legs', 'Sat', 'Hack squat · Glutes · Quads', [
        E('Hack Squat', 3, '8-12', 150),
        E('Hip Thrust', 3, '8-12', 120),
        E('Bulgarian Split Squat', 2, '10-12', 90),
        E('Seated Leg Curl', 3, '10-15', 75),
        E('Leg Extensions', 3, '12-15', 60),
        E('Seated Calf Raise', 3, '12-15', 60),
      ]),
    ],
  },
  {
    id: 'meso-fb3',
    name: 'Hypertrophy Meso: Full Body 3-Day',
    tagline: 'Every muscle three times a week, sets steered by your feedback.',
    description: 'A five-week mesocycle on three full-body days. Each exercise starts at its minimum effective volume with 3 reps in reserve; every week you get closer to failure, and your pump, soreness and joint ratings decide whether each muscle gets more sets. Week 5 is a deload, then the next meso starts again.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 3, minutes: 60, weeks: 5, split: 'Full body',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Run by the app: RIR 3 → 2 → 1 → 0, then a deload. Rate pump, soreness and joints after each workout: a good pump while recovered adds a set, still sore holds, joint pain holds and flags the exercise. Load goes up when every set reaches the top of the range.',
    cycle: CY.meso5,
    days: [
      D('Full Body A', 'Mon', 'Squat · Bench · Row', [
        M('Back Squat', 2, 4, '6-10', 150),
        M('Barbell Bench Press', 2, 4, '6-10', 150),
        M('Chest-Supported Row', 2, 4, '8-12', 120),
        M('Cable Lateral Raise', 2, 5, '12-20', 60),
        M('EZ-Bar Curl', 2, 4, '10-15', 60),
      ]),
      D('Full Body B', 'Wed', 'Hinge · Incline · Pulldown', [
        M('Romanian Deadlift', 2, 4, '8-12', 150),
        M('Incline Dumbbell Press', 2, 4, '8-12', 120),
        M('Lat Pulldown (wide)', 2, 4, '8-12', 120),
        M('Leg Extensions', 2, 4, '10-15', 75),
        M('Cable Triceps Pushdown', 2, 4, '10-15', 60),
      ]),
      D('Full Body C', 'Fri', 'Leg press · Press · Row', [
        M('Leg Press', 2, 4, '10-15', 120),
        M('Machine Shoulder Press', 2, 4, '8-12', 90),
        M('Cable Row (neutral)', 2, 4, '10-12', 90),
        M('Seated Leg Curl', 2, 4, '10-15', 75),
        M('Pec Deck', 2, 4, '12-15', 60),
      ]),
    ],
  },
  {
    id: 'min2-db',
    name: 'Minimalist 2-Day Dumbbell',
    tagline: 'Two 40-minute home sessions that cover every muscle.',
    description: 'For busy weeks at home: two full-body sessions with adjustable dumbbells and a bench. Each day has a squat or lunge, a press, a row, a hinge and a little arm or shoulder work. Take most sets within 1–2 reps of failure and twice a week is plenty to build and keep muscle.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'hypertrophy'],
    daysPerWeek: 2, minutes: 40, weeks: 8, split: 'Full body',
    equipment: ['dumbbell', 'bench'],
    progression: 'Double progression; when you outgrow a dumbbell, slow the lowering phase to 3 seconds or add a set before jumping weight.',
    days: [
      D('Day A', 'Mon', 'Squat · Press · Row', [
        E('Goblet Squat', 3, '8-15', 90),
        E('Flat DB Bench Press', 3, '8-12', 90),
        E('One-Arm Dumbbell Row', 3, '8-12', 75),
        E('Stiff-Legged Dumbbell Deadlift', 2, '10-12', 90),
        E('Side Lateral Raise', 2, '12-20', 45),
      ]),
      D('Day B', 'Thu', 'Split squat · Shoulders · Row', [
        E('Bulgarian Split Squat', 3, '8-12', 90),
        E('Seated Dumbbell Press', 3, '8-12', 90),
        E('Bent Over Two-Dumbbell Row', 3, '10-12', 75),
        E('Single Leg Glute Bridge', 2, '12-15', 60),
        E('Hammer Curl', 2, '10-12', 45, { ss: 'a' }),
        E('Lying Dumbbell Tricep Extension', 2, '10-12', 45, { ss: 'a' }),
      ]),
    ],
  },
  {
    id: 'db-ppl3',
    name: 'Dumbbell Push/Pull/Legs',
    tagline: 'A three-day split for a home gym with dumbbells and a bench.',
    description: 'Push, pull and legs with nothing but adjustable dumbbells and a bench. Each day trains its muscles with enough volume to grow (12–16 hard sets), using single-leg work and slow lowering to make moderate dumbbells count.',
    level: 'intermediate', levels: ['beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'],
    daysPerWeek: 3, minutes: 50, weeks: 8, split: 'Push / Pull / Legs',
    equipment: ['dumbbell', 'bench'],
    progression: 'Double progression. When a dumbbell gets easy, use a 3-second lowering phase or add a set before jumping to the next one.',
    days: [
      D('Push', 'Mon', 'Chest · Shoulders · Triceps', [
        E('Flat DB Bench Press', 4, '8-12', 90),
        E('Seated Dumbbell Press', 3, '8-12', 90),
        E('Incline Dumbbell Press', 3, '10-12', 75),
        E('Side Lateral Raise', 3, '12-20', 45),
        E('Lying Dumbbell Tricep Extension', 3, '10-12', 60),
      ]),
      D('Pull', 'Wed', 'Back · Rear delts · Biceps', [
        E('One-Arm Dumbbell Row', 4, '8-12', 75),
        E('Bent Over Two-Dumbbell Row', 3, '10-12', 75),
        E('Bent-Arm Dumbbell Pullover', 3, '10-12', 60),
        E('Reverse Flyes', 3, '12-15', 45),
        E('Incline Dumbbell Curl', 3, '10-12', 60),
        E('Hammer Curl', 2, '10-12', 60),
      ]),
      D('Legs', 'Fri', 'Squat · Hinge · Single leg', [
        E('Goblet Squat', 4, '8-12', 90),
        E('Stiff-Legged Dumbbell Deadlift', 3, '8-12', 90),
        E('Bulgarian Split Squat', 3, '8-12', 90),
        E('Dumbbell Step Ups', 2, '10-12', 75),
        E('Standing Dumbbell Calf Raise', 4, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'kb3',
    name: 'Kettlebell Strength 3-Day',
    tagline: 'One kettlebell, three sessions: swings, get-ups, squats and presses.',
    description: 'A complete program for a single kettlebell. Swings and cleans build the hinge and grip, get-ups and halos build shoulders that hold up, goblet squats and single-leg deadlifts cover the legs, and presses, rows and push-ups cover the upper body. Short rests keep it to about 40 minutes.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'strength'],
    daysPerWeek: 3, minutes: 40, weeks: 8, split: 'Full body',
    equipment: ['kettlebell'],
    progression: 'Add reps until you hit the top of every range, then move to the next kettlebell (or add a set while you wait for a heavier bell). Keep swings explosive: end a set when the hips slow down.',
    days: [
      D('Day A', 'Mon', 'Swing · Squat · Floor press', [
        E('One-Arm Kettlebell Swings', 5, '10-15', 60, { note: 'Switch hands each set.' }),
        E('Goblet Squat', 4, '8-12', 90),
        E('One-Arm Kettlebell Floor Press', 3, '8-12', 75),
        E('One-Arm Kettlebell Row', 3, '8-12', 75),
        E('Dead Bug', 3, '8-12', 45),
      ]),
      D('Day B', 'Wed', 'Get-up · Single-leg hinge · Press', [
        E('Kettlebell Turkish Get-Up (Lunge style)', 3, '2-3', 90, { note: 'Alternate sides every rep.' }),
        E('Kettlebell One-Legged Deadlift', 3, '8-10', 75),
        E('One-Arm Kettlebell Military Press To The Side', 3, '6-10', 75),
        E('Pushups', 3, '8-15', 60),
        E('Kettlebell Halo', 2, '8-10', 45),
      ]),
      D('Day C', 'Fri', 'Clean · Thruster · Row', [
        E('One-Arm Kettlebell Clean', 4, '5-8', 75),
        E('Kettlebell Thruster', 3, '8-10', 90),
        E('One-Arm Kettlebell Row', 3, '10-12', 60),
        E('One-Arm Kettlebell Swings', 3, '15-20', 60),
        E('Plank', 3, '30-45', 45),
      ]),
    ],
  },
  {
    id: 'kb2',
    name: 'Kettlebell Swing & Get-Up 2-Day',
    tagline: 'Twenty-five minutes, twice a week: swings, get-ups, squats and presses.',
    description: 'A minimalist kettlebell plan. One day is 100 one-arm swings and five get-ups; the other is goblet squats, presses and rows. The swing builds the hips and conditioning, the get-up builds shoulders and trunk control. Ideal on its own or next to sport and running.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general'],
    daysPerWeek: 2, minutes: 25, weeks: 8, split: 'Swing / Strength',
    equipment: ['kettlebell'],
    progression: 'Own the session first: when every set feels crisp and rests can drop to 30–45 s, use a heavier kettlebell for one set, then two, until the whole session uses it.',
    days: [
      D('Swings and get-ups', 'Tue', 'Swing · Get-up', [
        E('One-Arm Kettlebell Swings', 10, '10', 45, { note: 'Switch hands every set.' }),
        E('Kettlebell Turkish Get-Up (Squat style)', 5, '1-2', 60, { note: 'Alternate sides every rep.' }),
      ]),
      D('Squat, press, row', 'Sat', 'Goblet squat · Press · Row', [
        E('Goblet Squat', 5, '5-8', 75),
        E('One-Arm Kettlebell Military Press To The Side', 5, '5', 75),
        E('One-Arm Kettlebell Row', 5, '8-10', 60),
      ]),
    ],
  },
  {
    id: 'cali3',
    name: 'Calisthenics Foundations 3-Day',
    tagline: 'A pull-up bar and the floor: paired full-body sessions with clear progressions.',
    description: 'Three full-body sessions built from pairs: a pull with a leg exercise, a dip or push with a hinge, a row with a push-up, then core. Rest after each pair, not between its exercises. When a movement gets easy, move to the next variation (band-assisted → full pull-ups → weighted; incline → floor → feet-elevated push-ups).',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'hypertrophy', 'strength'],
    daysPerWeek: 3, minutes: 50, weeks: 8, split: 'Full body · pairs',
    equipment: ['pullup'],
    progression: 'Add reps until you reach the top of every range, then move to a harder variation (or add load with a backpack). Rest 90 s after each pair.',
    days: [
      D('Day A', 'Mon', 'Pull-up · Dip · Row', [
        E('Pullups', 3, '5-8', 30, { ss: 'a', note: 'Can\'t do 5 yet? Band-assisted pull-ups or slow negatives.' }),
        E('Bodyweight Squat', 3, '15-25', 90, { ss: 'a' }),
        E('Parallel Bar Dip', 3, '5-10', 30, { ss: 'b', note: 'Two sturdy chairs or a dip station.' }),
        E('Single Leg Glute Bridge', 3, '10-15', 90, { ss: 'b' }),
        E('Inverted Row', 3, '8-12', 30, { ss: 'c' }),
        E('Pushups', 3, '10-20', 90, { ss: 'c' }),
        E('Hanging Leg Raise', 3, '8-12', 60),
      ]),
      D('Day B', 'Wed', 'Chin-up · Push-up · Lunge', [
        E('Chin-Up', 3, '5-8', 30, { ss: 'a' }),
        E('Bodyweight Walking Lunge', 3, '10-12', 90, { ss: 'a' }),
        E('Push-Ups With Feet Elevated', 3, '8-15', 30, { ss: 'b' }),
        E('Hyperextensions With No Hyperextension Bench', 3, '10-15', 90, { ss: 'b' }),
        E('Inverted Row', 3, '8-12', 30, { ss: 'c' }),
        E('Dips - Triceps Version', 3, '6-12', 90, { ss: 'c' }),
        E('Plank', 3, '30-60', 45),
      ]),
      D('Day C', 'Fri', 'Pull-up · Step-up · Decline push-up', [
        E('Pullups', 4, '4-8', 30, { ss: 'a' }),
        E('Step-up with Knee Raise', 3, '10-12', 90, { ss: 'a' }),
        E('Decline Push-Up', 3, '8-15', 30, { ss: 'b' }),
        E('Butt Lift (Bridge)', 3, '12-20', 90, { ss: 'b' }),
        E('Inverted Row', 3, '8-12', 60),
        E('Reverse Crunch', 3, '10-15', 45),
      ]),
    ],
  },
  {
    id: 'cali4',
    name: 'Calisthenics Upper/Lower 4-Day',
    tagline: 'Harder bodyweight skills: handstand push-ups, single-leg squats, weighted pulls.',
    description: 'For lifters who own the basics. Upper days build toward weighted pull-ups and dips and handstand push-ups against a wall; lower days use single-leg squats to a box, glute-ham raises and jumps. Each movement has an easier and a harder version — train the one where the rep range is a real challenge.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'general', 'hypertrophy'],
    daysPerWeek: 4, minutes: 55, weeks: 10, split: 'Upper / Lower',
    equipment: ['pullup'],
    progression: 'Double progression on reps; at the top of a range, add load (a backpack or dip belt) or move to the harder variation. Keep 1–2 reps in reserve on the skill work.',
    days: [
      D('Upper A', 'Mon', 'Pull-up · Dip · Row', [
        E('Pullups', 4, '5-8', 150),
        E('Parallel Bar Dip', 4, '6-10', 120),
        E('Inverted Row', 3, '8-12', 90),
        E('Push-Ups With Feet Elevated', 3, '10-15', 75),
        E('Hanging Leg Raise', 3, '8-12', 60),
      ]),
      D('Lower A', 'Tue', 'Single-leg squat · Hamstrings', [
        E('Single-Leg High Box Squat', 4, '5-8', 120, { note: 'Lower the box as you get stronger; no box is a full pistol.' }),
        E('Natural Glute Ham Raise', 3, '3-6', 120),
        E('Bodyweight Walking Lunge', 3, '12-16', 75),
        E('Single Leg Glute Bridge', 3, '12-15', 60),
        E('Plank', 3, '45-60', 45),
      ]),
      D('Upper B', 'Thu', 'Handstand push-up · Chin-up', [
        E('Handstand Push-Ups', 4, '3-8', 150, { note: 'Against a wall; pike push-ups with feet on a box until you can.' }),
        E('Chin-Up', 4, '5-8', 150),
        E('Dips - Triceps Version', 3, '8-12', 90),
        E('Decline Push-Up', 3, '10-15', 75),
        E('Reverse Crunch', 3, '10-15', 45),
      ]),
      D('Lower B', 'Fri', 'Jump · Single-leg · Hinge', [
        E('Freehand Jump Squat', 4, '5', 90),
        E('Single-Leg High Box Squat', 3, '6-10', 120),
        E('Hyperextensions With No Hyperextension Bench', 3, '12-15', 75),
        E('Step-up with Knee Raise', 3, '10-12', 75),
        E('Dead Bug', 3, '10-12', 45),
      ]),
    ],
  },
  {
    id: 'glute3',
    name: 'Glute-Focused Full Body 3-Day',
    tagline: 'Glutes every session, balanced with upper-body work.',
    description: 'Three full-body days with the glutes trained from every angle: a heavy hip thrust or bridge, a hinge, a squat or lunge, and an isolation exercise every session, plus one push and one pull so the upper body keeps up. Great for anyone who wants stronger, fuller glutes on a three-day schedule.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'hypertrophy', goals: ['hypertrophy', 'general'], specialty: true,
    daysPerWeek: 3, minutes: 55, weeks: 10, split: 'Full body · glute focus',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Double progression: top of the range on every set, then add weight. Pause for one second at the top of every hip thrust and bridge.',
    days: [
      D('Day A', 'Mon', 'Hip thrust · RDL · Goblet squat', [
        E('Hip Thrust', 4, '8-12', 120),
        E('Romanian Deadlift', 3, '8-10', 120),
        E('Goblet Squat', 3, '10-12', 90),
        E('Lat Pulldown (wide)', 3, '10-12', 75),
        E('Standing DB Shoulder Press', 2, '10-12', 75),
        E('Thigh Abductor', 3, '15-20', 45),
      ]),
      D('Day B', 'Wed', 'Squat · Split squat · Kickback', [
        E('Back Squat', 3, '6-10', 150),
        E('Bulgarian Split Squat', 3, '8-12', 90),
        E('One-Legged Cable Kickback', 3, '12-15', 45),
        E('Chest-Supported Row', 3, '10-12', 75),
        E('Incline Dumbbell Press', 2, '10-12', 75),
        E('Seated Leg Curl', 3, '10-12', 60),
      ]),
      D('Day C', 'Fri', 'Sumo · Bridge · Step-up', [
        E('Sumo Deadlift', 3, '5-8', 150),
        E('Barbell Glute Bridge', 3, '10-15', 90),
        E('Dumbbell Step Ups', 3, '10-12', 75),
        E('Cable Row (neutral)', 3, '10-12', 75),
        E('Pull Through', 3, '12-15', 60),
        E('Dead Bug', 3, '8-12', 45),
      ]),
    ],
  },
  {
    id: 'joint3',
    name: 'Joint-Friendly Strength 3-Day',
    tagline: 'Strength for the long run: machines, trap bar and controlled reps.',
    description: 'Built for lifters over 40, coming back from time off, or working around cranky joints. Machines, cables, dumbbells and the trap bar replace the moves that load the spine and shoulders hardest. Moderate reps with 2–3 in reserve, a 2–3 second lowering phase, plus carries and anti-rotation core work for everyday strength.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'strength'], specialty: true,
    daysPerWeek: 3, minutes: 50, weeks: 10, split: 'Full body',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    progression: 'Double progression with 2–3 reps in reserve. Add load only when every set at the top of the range feels smooth and pain-free; otherwise add a rep or hold.',
    days: [
      D('Day A', 'Mon', 'Leg press · Chest press · Row', [
        E('Leg Press', 3, '10-12', 120),
        E('Machine Bench Press', 3, '10-12', 90),
        E('Cable Row (neutral)', 3, '10-12', 90),
        E('Seated Leg Curl', 2, '12-15', 60),
        E('Face Pull', 2, '15-20', 45),
        E('Pallof Press', 3, '10-12', 45),
      ]),
      D('Day B', 'Wed', 'Trap bar · Incline · Pulldown', [
        E('Trap Bar Deadlift', 3, '5-8', 150),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Lat Pulldown (wide)', 3, '10-12', 90),
        E('Dumbbell Squat To A Bench', 2, '10-12', 90),
        E('Cable Lateral Raise', 2, '12-15', 45),
        E('Farmer\'s Walk', 3, '30-40', 75, { note: 'Walk 30–40 m per set: log the metres as reps.' }),
      ]),
      D('Day C', 'Fri', 'Step-up · Press · Row', [
        E('Dumbbell Step Ups', 3, '8-12', 90),
        E('Flat DB Bench Press', 3, '8-12', 90),
        E('Chest-Supported Row', 3, '10-12', 75),
        E('Single Leg Glute Bridge', 2, '12-15', 60),
        E('Hammer Curl', 2, '10-12', 45, { ss: 'a' }),
        E('Cable Triceps Pushdown', 2, '10-12', 45, { ss: 'a' }),
        E('Dead Bug', 2, '8-10', 45),
      ]),
    ],
  },
  {
    id: 'active2',
    name: 'Active Aging 2-Day',
    tagline: 'Two easy-to-learn sessions for strength, balance and confidence.',
    description: 'Two full-body sessions on machines, cables and light dumbbells: leg strength for stairs and chairs, pushing and pulling for daily life, step-ups and carries for balance and grip. Every exercise is easy to learn and easy to scale. Leave a couple of reps in the tank — consistency beats intensity here.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general'], specialty: true,
    daysPerWeek: 2, minutes: 40, weeks: 12, split: 'Full body',
    equipment: ['dumbbell', 'bench', 'cable', 'machine'],
    progression: 'When you can do the top of the range on every set with good control, add the smallest weight step. Two to three reps in reserve on every set.',
    days: [
      D('Day A', 'Tue', 'Legs · Push · Pull · Balance', [
        E('Leg Press', 2, '10-15', 90),
        E('Machine Bench Press', 2, '10-15', 75),
        E('Lat Pulldown (wide)', 2, '10-15', 75),
        E('Dumbbell Step Ups', 2, '8-10', 60, { note: 'Use a low step and a rail or wall for balance at first.' }),
        E('Pallof Press', 2, '10-12', 45),
        E('Farmer\'s Walk', 2, '20-30', 60, { note: 'Walk 20–30 m per set: log the metres as reps.' }),
      ]),
      D('Day B', 'Fri', 'Sit-to-stand · Press · Row · Core', [
        E('Dumbbell Squat To A Bench', 2, '10-12', 90),
        E('Seated Dumbbell Press', 2, '10-12', 75),
        E('Cable Row (neutral)', 2, '10-15', 75),
        E('Butt Lift (Bridge)', 2, '12-15', 60),
        E('Dead Bug', 2, '8-10', 45),
        E('Standing Dumbbell Calf Raise', 2, '12-15', 45),
      ]),
    ],
  },
  {
    id: 'pb5-top',
    name: 'Top Set Powerbuilding 5-Day',
    tagline: 'A heavy top set and back-offs on each big lift, bodybuilding after.',
    description: 'Five days: one big lift each (squat, bench, deadlift, overhead press) plus an arms-and-pump day. Each lift is one top set at a target RPE — 7, 8, then 9 across three weeks — followed by back-off sets 10% lighter that the app recalculates from today\'s top set. Week 4 is a deload. Hypertrophy work fills the rest of each session.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'powerbuilding', goals: ['powerbuilding', 'strength', 'hypertrophy'],
    daysPerWeek: 5, minutes: 70, weeks: 4, split: 'Squat / Bench / Deadlift / Press / Arms',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Run by the app: log RPE on each top set and the back-offs follow it live. Your e1RM updates every session and next week\'s top set comes from it. RPE 7 → 8 → 9, deload in week 4, repeat.',
    cycle: CY.rpe4,
    days: [
      D('Squat', 'Mon', 'Squat top set · Legs', [
        R('Back Squat', 3, 3, 5, 240, { rpe: [7, 8, 9, 6] }),
        E('Romanian Deadlift', 3, '8-10', 150),
        E('Leg Press', 3, '10-12', 120),
        E('Seated Leg Curl', 3, '10-12', 75),
        E('Standing Calf Raise', 3, '10-15', 60),
      ]),
      D('Bench', 'Tue', 'Bench top set · Back', [
        R('Barbell Bench Press', 3, 3, 5, 210, { rpe: [7, 8, 9, 6] }),
        E('Weighted Pull-up', 4, '6-8', 120),
        E('Seated Dumbbell Press', 3, '8-12', 90),
        E('Chest-Supported Row', 3, '10-12', 75),
        E('Cable Triceps Pushdown', 3, '10-12', 60),
      ]),
      D('Deadlift', 'Thu', 'Deadlift top set · Front squat', [
        R('Conventional Deadlift', 3, 2, 5, 270, { rpe: [7, 8, 9, 6] }),
        E('Front Squat', 3, '6-8', 150),
        E('Hip Thrust', 3, '8-12', 120),
        E('Hanging Leg Raise', 3, '10-12', 60),
      ]),
      D('Press', 'Fri', 'Press top set · Upper', [
        R('Overhead Press', 5, 2, 8, 180, { rpe: [7, 8, 9, 6] }),
        E('Incline Dumbbell Press', 3, '8-12', 90),
        E('Lat Pulldown (wide)', 3, '10-12', 75),
        E('Cable Lateral Raise', 4, '12-20', 45),
        E('EZ-Bar Curl', 3, '8-12', 60),
      ]),
      D('Arms and pump', 'Sat', 'Close-grip · Rows · Arms', [
        E('Close-Grip Barbell Bench Press', 3, '6-10', 120),
        E('Cable Row (neutral)', 3, '10-12', 75),
        E('Pec Deck', 3, '12-15', 60),
        E('Face Pull', 3, '15-20', 45),
        E('Preacher Curl', 3, '10-12', 60, { ss: 'a' }),
        E('Overhead Cable Extension', 3, '10-12', 60, { ss: 'a' }),
      ]),
    ],
  },
  {
    id: 'peak8',
    name: '8-Week Meet Peak',
    tagline: 'A short peak: volume, heavy triples, singles, taper, test.',
    description: 'Eight weeks to a max-out day for squat, bench and deadlift. Two volume weeks and a strength week build the base, then a deload; heavy triples and singles follow, a two-set taper sheds fatigue, and week 8 works up to a new max. A bench volume day keeps bench practice frequent. For lifters who already know a training max on each lift.',
    level: 'advanced', levels: ['intermediate', 'advanced'],
    goal: 'strength', goals: ['strength'],
    daysPerWeek: 4, minutes: 70, weeks: 8, split: 'Squat / Bench / Deadlift / Bench',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'pullup'],
    progression: 'Week-by-week percentages of your training max with a deload in week 4 and a taper in week 7. Week 8 tests a heavy single; the app sets your next training max to 90% of the new estimated max.',
    cycle: CY.peak8,
    days: [
      D('Squat', 'Mon', 'Squat · Hinge accessory', [
        P('Back Squat', 'peak8', 240),
        E('Romanian Deadlift', 3, '6-8', 150),
        E('Seated Leg Curl', 2, '8-12', 75),
      ]),
      D('Bench', 'Tue', 'Bench · Upper back', [
        P('Barbell Bench Press', 'peak8', 210),
        E('Chest-Supported Row', 4, '8-10', 90),
        E('Face Pull', 3, '15-20', 45),
      ]),
      D('Deadlift', 'Thu', 'Deadlift · Core', [
        P('Conventional Deadlift', 'peak8', 270),
        E('Front Squat', 3, '4-6', 150),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
      D('Bench volume', 'Sat', 'Bench volume · Pull-ups · Triceps', [
        P('Barbell Bench Press', 'dupMedium', 150),
        E('Weighted Pull-up', 3, '5-8', 120),
        E('Cable Triceps Pushdown', 3, '10-12', 60),
      ]),
    ],
  },
  {
    id: 'strongman3',
    name: 'Strongman-Lite 3-Day',
    tagline: 'Trap bar, push press, carries and sled: strength that transfers.',
    description: 'Strongman training for a regular gym. Heavy trap-bar pulls, push presses and Zercher squats build whole-body strength; farmer\'s walks and sled pushes build grip, trunk and work capacity. Three days a week with rows and chin-ups to keep the upper back balanced. No sled? Push a loaded prowler, or swap in hill sprints.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'powerbuilding', 'general'], specialty: true,
    daysPerWeek: 3, minutes: 60, weeks: 8, split: 'Full body · events',
    equipment: ['barbell', 'dumbbell', 'pullup'],
    progression: 'Double progression on the lifts (top of the range, then add weight). For carries and the sled, add distance first, then load.',
    days: [
      D('Pull and press', 'Mon', 'Trap bar · Push press · Carry', [
        E('Trap Bar Deadlift', 4, '3-5', 210),
        E('Push Press', 4, '3-5', 180),
        E('Farmer\'s Walk', 4, '30-40', 120, { note: 'Walk 30–40 m per set: log the metres as reps.' }),
        E('Chin-Up', 3, '6-10', 90),
        E('Ab Wheel Rollout', 3, '8-12', 60),
      ]),
      D('Squat and sled', 'Wed', 'Zercher · Clean and press · Sled', [
        E('Zercher Squats', 3, '5-8', 180, { note: 'Bar in the crook of your elbows; a towel or pad helps.' }),
        E('Clean and Press', 5, '3', 150),
        E('Sled Push', 6, '20-30', 90, { note: '20–30 m per push: log the metres as reps.' }),
        E('Bent Over Barbell Row', 3, '6-10', 90),
        E('Dumbbell Shrug', 3, '10-15', 60),
      ]),
      D('Rack pull and overhead', 'Fri', 'Front squat · Press · Rack pull', [
        E('Front Squat', 3, '5', 180),
        E('Overhead Press', 4, '5-8', 150),
        E('Rack Pulls', 3, '3-5', 180),
        E('Farmer\'s Walk', 3, '40-60', 120, { note: 'Walk 40–60 m per set: log the metres as reps.' }),
        E('Plank', 3, '45-60', 45),
      ]),
    ],
  },
  {
    id: 'athlete3',
    name: 'Athletic Power 3-Day',
    tagline: 'Jump, lift fast, then lift heavy: power first, strength second.',
    description: 'For field and court athletes. Every session starts fresh with a jump and an Olympic or ballistic lift for speed, then a heavy compound for strength, single-leg work for knees and hips, and trunk or carry work. Low reps and full rest on the power work — quality over fatigue.',
    level: 'intermediate', levels: ['beginner', 'intermediate', 'advanced'],
    goal: 'strength', goals: ['strength', 'general'], specialty: true,
    daysPerWeek: 3, minutes: 60, weeks: 8, split: 'Full body · power',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'pullup'],
    progression: 'Power work never goes to failure: keep every rep fast and end a set when speed drops. Strength lifts use double progression.',
    days: [
      D('Lower power', 'Mon', 'Box jump · Clean · Squat', [
        E('Box Jump (Multiple Response)', 4, '3', 90),
        E('Power Clean', 5, '3', 150),
        E('Back Squat', 4, '4-6', 180),
        E('Bulgarian Split Squat', 3, '6-8', 90),
        E('Pallof Press', 3, '10-12', 45),
      ]),
      D('Upper power', 'Wed', 'Bound · Push press · Pull-up', [
        E('Lateral Bound', 4, '4', 75, { note: 'Reps per side; stick each landing for a second.' }),
        E('Push Press', 4, '4-6', 150),
        E('Weighted Pull-up', 4, '5-8', 120),
        E('Romanian Deadlift', 3, '6-8', 120),
        E('Hanging Leg Raise', 3, '8-12', 60),
      ]),
      D('Total body', 'Fri', 'Long jump · Trap bar · Bench', [
        E('Standing Long Jump', 4, '3', 90),
        E('Trap Bar Deadlift', 4, '3-5', 180),
        E('Barbell Bench Press', 4, '4-6', 150),
        E('Dumbbell Step Ups', 3, '6-8', 75),
        E('Farmer\'s Walk', 3, '30-40', 90, { note: 'Walk 30–40 m per set: log the metres as reps.' }),
      ]),
    ],
  },
  {
    id: 'express20-db',
    name: '20-Minute Dumbbell Express',
    tagline: 'Two supersets a session at home: 20 minutes, done.',
    description: 'For the busiest weeks. Each session is two supersets of a lower-body and an upper-body exercise, three rounds each, with short rests. Every muscle is trained three times a week in about 20 minutes, with just dumbbells and a bench.',
    level: 'beginner', levels: ['new', 'beginner', 'intermediate'],
    goal: 'general', goals: ['general', 'hypertrophy'], specialty: true,
    daysPerWeek: 3, minutes: 20, weeks: 8, split: 'Full body · supersets',
    equipment: ['dumbbell', 'bench'],
    progression: 'Double progression; take the last round of each superset within a rep or two of failure.',
    days: [
      D('Express A', 'Mon', 'Squat · Row · Press · Hinge', [
        E('Goblet Squat', 3, '10-15', 20, { ss: 'a' }),
        E('One-Arm Dumbbell Row', 3, '10-12', 60, { ss: 'a' }),
        E('Flat DB Bench Press', 3, '8-12', 20, { ss: 'b' }),
        E('Stiff-Legged Dumbbell Deadlift', 3, '10-12', 60, { ss: 'b' }),
      ]),
      D('Express B', 'Wed', 'Lunge · Press · Row · Glutes', [
        E('Dumbbell Lunges', 3, '10-12', 20, { ss: 'a' }),
        E('Seated Dumbbell Press', 3, '8-12', 60, { ss: 'a' }),
        E('Bent Over Two-Dumbbell Row', 3, '10-12', 20, { ss: 'b' }),
        E('Single Leg Glute Bridge', 3, '12-15', 60, { ss: 'b' }),
      ]),
      D('Express C', 'Fri', 'Split squat · Incline · Arms', [
        E('Bulgarian Split Squat', 3, '8-12', 20, { ss: 'a' }),
        E('Incline Dumbbell Press', 3, '8-12', 60, { ss: 'a' }),
        E('Dumbbell Bicep Curl', 2, '10-15', 20, { ss: 'b' }),
        E('Lying Dumbbell Tricep Extension', 2, '10-15', 60, { ss: 'b' }),
      ]),
    ],
  },
  {
    id: 'express20-bw',
    name: '20-Minute Bodyweight Circuit',
    tagline: 'No equipment, 20 minutes, three times a week.',
    description: 'Short circuits for travel or home: a leg exercise, a push, a pull or hinge, and core, done back to back with short rests. It builds a base of strength and conditioning, and every exercise has an easier and a harder version so it grows with you.',
    level: 'beginner', levels: ['new', 'beginner'],
    goal: 'general', goals: ['general'], specialty: true,
    daysPerWeek: 3, minutes: 20, weeks: 6, split: 'Full body · circuits',
    equipment: [],
    progression: 'Add reps each week; at the top of a range, move to the harder variation (incline → floor → feet-elevated push-ups; squats → split squats).',
    days: [
      D('Circuit A', 'Mon', 'Squat · Push-up · Row · Plank', [
        E('Bodyweight Squat', 3, '15-20', 15, { ss: 'a' }),
        E('Pushups', 3, '8-15', 15, { ss: 'a' }),
        E('Inverted Row', 3, '8-12', 15, { ss: 'a', note: 'Under a sturdy table.' }),
        E('Plank', 3, '30-45', 60, { ss: 'a' }),
      ]),
      D('Circuit B', 'Wed', 'Lunge · Incline push-up · Bridge', [
        E('Bodyweight Walking Lunge', 3, '10-12', 15, { ss: 'a' }),
        E('Incline Push-Up', 3, '12-20', 15, { ss: 'a' }),
        E('Butt Lift (Bridge)', 3, '15-20', 15, { ss: 'a' }),
        E('Dead Bug', 3, '10-12', 60, { ss: 'a' }),
      ]),
      D('Circuit C', 'Fri', 'Step-up · Decline push-up · Back', [
        E('Step-up with Knee Raise', 3, '10-12', 15, { ss: 'a' }),
        E('Push-Ups With Feet Elevated', 3, '6-12', 15, { ss: 'a' }),
        E('Hyperextensions With No Hyperextension Bench', 3, '12-15', 15, { ss: 'a' }),
        E('Mountain Climbers', 3, '20-30', 60, { ss: 'a' }),
      ]),
    ],
  },
  {
    id: 'gvt3',
    name: '10×10 Volume Block, 3-Day',
    tagline: 'Ten sets of ten on paired lifts: a short, brutal hypertrophy block.',
    description: 'Each day pairs two opposing lifts for 10 sets of 10 at about 60% of your max with short rests, then finishes with two lighter accessories for 3 sets. Same weight for all ten sets: the early sets feel easy, the last ones do not. Run it for 4–6 weeks, then return to lower-volume training.',
    level: 'intermediate', levels: ['intermediate', 'advanced'],
    goal: 'hypertrophy', goals: ['hypertrophy'],
    daysPerWeek: 3, minutes: 60, weeks: 6, split: 'Chest-back / Legs / Arms-shoulders',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup'],
    progression: 'Start at about 60% of your 1RM. When you complete all 10×10, add 2.5–5% next session.',
    days: [
      D('Chest and back', 'Mon', 'Bench + Row 10×10', [
        E('Barbell Bench Press', 10, '10', 45, { ss: 'a' }),
        E('Bent Over Barbell Row', 10, '10', 75, { ss: 'a' }),
        E('Incline Dumbbell Press', 3, '10-12', 45, { ss: 'b' }),
        E('Chest-Supported Row', 3, '10-12', 75, { ss: 'b' }),
      ]),
      D('Legs and abs', 'Wed', 'Squat + Leg curl 10×10', [
        E('Back Squat', 10, '10', 45, { ss: 'a' }),
        E('Lying Leg Curl', 10, '10', 75, { ss: 'a' }),
        E('Hanging Leg Raise', 3, '10-15', 45, { ss: 'b' }),
        E('Standing Calf Raise', 3, '12-15', 60, { ss: 'b' }),
      ]),
      D('Arms and shoulders', 'Fri', 'Dip + Curl 10×10', [
        E('Dips - Triceps Version', 10, '10', 45, { ss: 'a' }),
        E('Hammer Curl', 10, '10', 75, { ss: 'a' }),
        E('Side Lateral Raise', 3, '10-12', 45, { ss: 'b' }),
        E('Reverse Flyes', 3, '10-12', 60, { ss: 'b' }),
      ]),
    ],
  },
];

export const TEMPLATE_BY_ID = Object.fromEntries(TEMPLATES.map(t => [t.id, t]));

// ——— Stats ———————————————————————————————————————————————————————————————

export function templateStats(t) {
  const exCount = t.days.reduce((a, d) => a + d.exercises.length, 0);
  const setsPerWeek = t.days.reduce((a, d) => a + d.exercises.reduce((b, e) => b + e.sets, 0), 0);
  return { exCount, setsPerWeek, avgExercises: Math.round(exCount / t.days.length) };
}

// Hard sets per week per muscle (primary 1, secondary 0.5). Needs resolved exercises.
export function templateVolume(t, exercises = [], catalog = null) {
  const out = {};
  for (const d of t.days) for (const e of d.exercises) {
    const ex = resolveTemplateExercise(e.x, exercises, catalog);
    if (!ex) continue;
    const { primary, secondary } = musclesFor(ex);
    primary.forEach(m => { out[m] = (out[m] || 0) + e.sets; });
    secondary.forEach(m => { out[m] = (out[m] || 0) + e.sets * 0.5; });
  }
  return Object.entries(out).map(([muscle, sets]) => ({ muscle, sets: Math.round(sets * 2) / 2 }))
    .sort((a, b) => b.sets - a.sets);
}

// ——— Resolution ——————————————————————————————————————————————————————————

const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Exact (normalized) name match in the user's library, then the catalog, then fuzzy.
export function resolveTemplateExercise(name, exercises = [], catalog = null) {
  const n = normName(name);
  const lib = exercises.find(e => normName(e.name) === n);
  if (lib) return { ...lib, source: 'library' };
  if (catalog) {
    const c = catalog.find(e => normName(e.name) === n);
    if (c) return { ...c, source: 'catalog' };
  }
  return resolveExercise(name, exercises, catalog);
}

// ——— Recommendation ——————————————————————————————————————————————————————

const LEVEL_RANK = { new: 0, beginner: 1, intermediate: 2, advanced: 3 };

export function profileEquipment(profile) {
  if (!profile) return null;
  if (Array.isArray(profile.equipment) && profile.equipment.length) return profile.equipment;
  if (profile.equipmentPreset) return equipmentForPreset(profile.equipmentPreset) || [];
  if (Array.isArray(profile.equipment)) return profile.equipment; // [] = bodyweight only
  return null;
}

function joinPhrase(list) {
  if (list.length <= 1) return list[0] || '';
  return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
}

// Score one template against a profile. Missing profile fields are neutral.
export function scoreTemplate(t, profile = {}) {
  const reasons = [];
  const warnings = [];
  let score = 0;
  const p = profile || {};

  // Equipment (hard requirement)
  const have = profileEquipment(p);
  let equipOk = true;
  if (have) {
    const missing = t.equipment.filter(e => !have.includes(e));
    if (missing.length) {
      equipOk = false;
      score -= 120;
      warnings.push(`Needs ${joinPhrase(missing.map(m => EQUIP_PHRASE[m] || m))}`);
    } else {
      score += 20;
      // A home program for someone with a full gym is a weaker fit.
      const unused = have.filter(e => !t.equipment.includes(e)).length;
      if (t.equipment.length === 0) reasons.push('No equipment needed');
      else if (have.length <= 3) reasons.push(`Uses only ${joinPhrase(t.equipment.map(m => EQUIP_PHRASE[m] || m))}`);
      score -= Math.min(30, unused * 6);
      // Someone with a full gym shouldn't be steered to a home (dumbbell-only or
      // bodyweight) program: those exist for people whose equipment is limited.
      const gymKit = ['barbell', 'machine', 'cable'];
      const hasGym = have.filter(e => gymKit.includes(e)).length >= 2;
      // Someone who owns equipment would rather use it than a no-equipment plan.
      if (!t.equipment.length && have.length >= 2) score -= 10;
      if (hasGym && !t.equipment.some(e => gymKit.includes(e))) score -= 60;
      else if (hasGym && !t.equipment.includes('barbell') && have.includes('barbell')) score -= 8;
    }
  }

  // Days per week
  if (p.daysPerWeek) {
    const diff = t.daysPerWeek - p.daysPerWeek;
    if (diff === 0) { score += 40; reasons.unshift(`${t.daysPerWeek} days a week, as you asked`); }
    else if (Math.abs(diff) === 1) { score += 14; reasons.push(`${t.daysPerWeek} days a week (${diff < 0 ? 'one fewer' : 'one more'} than you picked)`); }
    else score -= 12 * Math.abs(diff);
  }

  // Goal
  if (p.goal) {
    const phrase = GOAL_PHRASE[p.goal] || p.goal;
    if (t.goal === p.goal) { score += 30; reasons.push(`Built for ${phrase}`); }
    else if (t.goals.includes(p.goal)) { score += 16; reasons.push(`Also works for ${phrase}`); }
    else score -= 10;
    if (t.specialty) score -= 16;
  }

  // Experience
  if (p.experience) {
    const lv = LEVEL_RANK[p.experience] ?? 1;
    if (t.levels.includes(p.experience)) {
      score += 22;
      if (p.experience === 'new' || p.experience === 'beginner') reasons.push('Beginner-friendly exercises');
      else reasons.push(`Right volume for ${p.experience === 'advanced' ? 'an advanced' : 'an intermediate'} lifter`);
    } else {
      const tl = Math.min(...t.levels.map(l => LEVEL_RANK[l]));
      const th = Math.max(...t.levels.map(l => LEVEL_RANK[l]));
      const gap = lv < tl ? tl - lv : lv - th;
      score -= gap * (lv < tl ? 18 : 10); // too advanced is worse than too easy
      if (lv < tl) warnings.push('More volume than most beginners need');
    }
  }

  // Session length
  if (p.sessionMinutes) {
    const over = t.minutes - p.sessionMinutes;
    if (over <= 0) { score += 10; reasons.push(`About ${t.minutes} min per session`); }
    else if (over <= 10) score += 2;
    else { score -= Math.min(30, over); warnings.push(`Sessions run about ${t.minutes} min`); }
  }

  return { score, reasons: reasons.slice(0, 3), warnings, equipOk };
}

// Ranked list (best first). `fit` is 0–100 for display.
export function recommendTemplates(profile = {}, { limit = TEMPLATES.length } = {}) {
  const scored = TEMPLATES.map(t => ({ template: t, ...scoreTemplate(t, profile) }));
  scored.sort((a, b) => (b.equipOk - a.equipOk) || (b.score - a.score));
  const max = 40 + 20 + 30 + 22 + 10;
  return scored.slice(0, limit).map(s => ({ ...s, fit: Math.max(0, Math.min(100, Math.round((s.score / max) * 100))) }));
}

// ——— Starting weights ——————————————————————————————————————————————————————

// Typical working weight (lb) for an intermediate lifter at ~8–10 reps.
// Per-hand for dumbbells. 0 = bodyweight / added load, to be found.
const STD_LB = {
  'back squat': 185, 'front squat': 135, 'conventional deadlift': 225, 'romanian deadlift': 155,
  'barbell bench press': 155, 'overhead press': 95, 'bent over barbell row': 135,
  'close grip barbell bench press': 135, 'barbell lunge': 95, 'hip thrust': 185, 'barbell glute bridge': 185,
  'barbell curl': 65, 'ez bar curl': 55, 'skull crushers': 55, 'incline smith press': 115,
  'leg press': 270, 'hack squat': 180, 'machine bench press': 120, 'machine shoulder press': 90,
  'lat pulldown wide': 120, 'close grip front lat pulldown': 120, 'cable row neutral': 120,
  'chest supported row': 45, 'flat db bench press': 55, 'incline dumbbell press': 50,
  'standing db shoulder press': 35, 'seated dumbbell press': 35, 'arnold dumbbell press': 30,
  'one arm dumbbell row': 55, 'bent over two dumbbell row': 40, 'goblet squat': 45,
  'stiff legged dumbbell deadlift': 45, 'dumbbell lunges': 30, 'dumbbell step ups': 25,
  'bulgarian split squat': 30, 'dumbbell floor press': 45, 'bent arm dumbbell pullover': 40,
  'dumbbell flyes': 25, 'reverse flyes': 15, 'side lateral raise': 15, 'dumbbell shrug': 55,
  'dumbbell bicep curl': 25, 'hammer curl': 25, 'incline dumbbell curl': 20,
  'lying dumbbell tricep extension': 20, 'standing dumbbell calf raise': 40,
  'pec deck': 100, 'cable flies': 25, 'cable lateral raise': 12.5, 'face pull': 40,
  'cable rear delt fly': 15, 'reverse machine flyes': 70, 'straight arm pulldown': 50,
  'seated leg curl': 80, 'lying leg curl': 70, 'leg extensions': 90, 'standing calf raise': 140,
  'seated calf raise': 70, 'preacher curl': 45, 'standing biceps cable curl': 40,
  'cable triceps pushdown': 45, 'overhead cable extension': 45, 'cable crunch': 70,
  'thigh abductor': 90, 'one legged cable kickback': 20, 'pull through': 50,
};
const DEADLIFTISH = new Set(['conventional deadlift', 'romanian deadlift']);
const EXP_SCALE = { beginner: 0.6, intermediate: 1, advanced: 1.3 };

function fallbackStd(ex) {
  const eq = ex.equipment || '';
  const comp = ex.mechanic === 'compound';
  if (/^Bodyweight|Other|Band/.test(eq)) return 0;
  if (eq === 'Barbell' || eq === 'Smith' || eq === 'EZ Bar') return comp ? 115 : 55;
  if (eq === 'Dumbbell' || eq === 'Kettlebell') return comp ? 40 : 20;
  if (eq === 'Machine') return comp ? 140 : 80;
  if (eq === 'Cable') return comp ? 100 : 35;
  return 0;
}

// Round a lb weight to a loadable increment in the user's display units.
// Same grid as progression.js roundTo (2.5 / 1.25 kg, 5 / 2.5 lb).
export function roundLoad(lb, units = 'lb', small = false) {
  return roundTo(lb, units, small);
}

// Starting weight for one template exercise, in lb. Uses the lifter's own history
// first (an e1RM from their recent sessions), then a strength-standard estimate
// scaled by experience. Brand-new lifters start with the empty bar on barbell
// lifts and "find your weight" (0) elsewhere.
export function startingWeight(e, ex, { profile, units = 'lb', state } = {}) {
  if (!ex || ex.unit === 'sec') return 0;
  const key = normName(ex.name);
  // On the lifter's own bar, plates and dumbbells (Settings → Plates & bar).
  const kind = equipmentKind({ name: ex.name, equipment: ex.equipment });
  const roundLoad = (lb, u, small) => loadable(lb, { kind, prefs: state?.prefs || null, units: u, small });
  if (state && ex.id) {
    const h = exerciseHistory(state, ex.id).slice(-3);
    const top = h.reduce((b, x) => ((x.e1rm || 0) > (b?.e1rm || 0) ? x : b), null);
    if (top && top.e1rm > 0) {
      // e1RMs of bodyweight lifts are effective loads (body weight ± added):
      // convert back to the number the lifter logs (added load / assistance).
      const eff = loadFor(top.e1rm, e.repMin, 8, 1);
      const raw = top.load ? toLoggedLoad(eff, top.load, top.bw) : eff;
      return raw > 0 ? roundLoad(raw, units, raw < 40) : 0;
    }
  }
  const exp = profile?.experience;
  const std = STD_LB[key] ?? fallbackStd(ex);
  if (!std) return 0;
  if (exp === 'new') {
    if ((ex.equipment === 'Barbell') && ex.mechanic === 'compound' && !/hip thrust|glute bridge/.test(key)) {
      return DEADLIFTISH.has(key) ? roundLoad(95, units) : roundLoad(45, units);
    }
    return 0;
  }
  const scale = EXP_SCALE[exp];
  if (!scale) return 0; // unknown experience → find it in week one
  const mid = (e.repMin + e.repMax) / 2;
  const repAdj = 1.3 / (1 + mid / 30); // Epley ratio vs a 9-rep set
  const raw = std * scale * repAdj;
  return roundLoad(raw, units, raw < 40);
}

// ——— Builder ——————————————————————————————————————————————————————————————

let _n = 0;
const lid = (p) => p + Date.now().toString(36).slice(-4) + (++_n).toString(36) + Math.random().toString(36).slice(2, 5);

// → { program, newExercises (state-shaped, pass to actions.ensureExercises first), missing[] }
export function buildProgramFromTemplate(template, { exercises = [], catalog = null, profile = null, units = 'lb', state = null } = {}) {
  const newExercises = [];
  const missing = [];
  const tms = {};
  const pool = () => [...exercises, ...newExercises];
  const days = template.days.map(d => {
    const ssMap = {};
    return {
      id: lid('day_'),
      name: d.name,
      day: d.day,
      exercises: d.exercises.map(e => {
        let ex = resolveTemplateExercise(e.x, pool(), catalog);
        if (ex && ex.source === 'catalog') {
          const stateShaped = catalogToExercise(ex);
          if (!pool().some(p => p.id === stateShaped.id)) newExercises.push(stateShaped);
          ex = stateShaped;
        }
        if (!ex) missing.push(e.x);
        let supersetId = null;
        if (e.ss) supersetId = ssMap[e.ss] || (ssMap[e.ss] = lid('ss_'));
        const pe = {
          id: lid('pe_'),
          exerciseId: ex ? ex.id : null,
          name: ex ? ex.name : e.x,
          group: ex ? (ex.group || 'Other') : 'Other',
          unit: (ex && ex.unit) || 'reps',
          sets: e.sets,
          repMin: e.repMin,
          repMax: e.repMax,
          targetWeight: e.scheme === 'percent' ? 0
            : startingWeight(e.scheme === 'rpe_topset' ? { ...e, repMin: e.cfg.topReps, repMax: e.cfg.topReps } : e, ex, { profile, units, state }),
          rest: e.rest,
          yt: (ex && ex.yt) || '',
          alternates: [],
        };
        if (supersetId) pe.supersetId = supersetId;
        if (e.note) pe.note = e.note;
        if (e.scheme && e.scheme !== 'double') {
          pe.scheme = e.scheme;
          const cfg = { ...(e.cfg || {}) };
          if (cfg.tmOf) {
            let ref = resolveTemplateExercise(cfg.tmOf, pool(), catalog);
            if (ref && ref.source === 'catalog') {
              const shaped = catalogToExercise(ref);
              if (!pool().some(x => x.id === shaped.id)) newExercises.push(shaped);
              ref = shaped;
            }
            if (ref) { cfg.tmKey = ref.id; cfg.tmName = ref.name; }
            delete cfg.tmOf;
          }
          pe.schemeCfg = cfg;
          pe.schemeState = {};
          if (e.scheme === 'percent') {
            const key = cfg.tmKey || pe.exerciseId;
            if (key && !(key in tms)) tms[key] = historyTM(key, state, units);
          }
        }
        return pe;
      }),
    };
  });
  return {
    program: {
      name: template.name,
      author: 'Lift Log library',
      templateId: template.id,
      weeks: template.weeks || 8,
      currentWeek: 1,
      split: template.split,
      description: template.tagline,
      progressionNote: template.progression,
      days,
      ...(template.cycle ? { cycle: template.cycle } : {}),
      ...(Object.keys(tms).length ? { tms: Object.fromEntries(Object.entries(tms).filter(([, v]) => v > 0)) } : {}),
      startedAt: Date.now(),
    },
    newExercises,
    missing,
  };
}

// Training max (lb) from the lifter's own recent history: 90 % of the best e1RM
// in their last three sessions of that lift. 0 when there's no history.
function historyTM(exerciseId, state, units) {
  if (!state || !exerciseId) return 0;
  const h = exerciseHistory(state, exerciseId).slice(-3);
  const top = h.reduce((b, x) => ((x.e1rm || 0) > (b?.e1rm || 0) ? x : b), null);
  if (!top || !(top.e1rm > 0)) return 0;
  const tm = top.load ? toLoggedLoad(top.e1rm * 0.9, top.load, top.bw) : top.e1rm * 0.9;
  return tm > 0 ? roundTo(tm, units) : 0;
}

// Lifts whose training max the lifter must enter before starting (none known).
export const templateNeedsTMs = (program) => missingTMs(program);

// Short scheme summary for a template card/detail, e.g. ['Training max %', 'Linear'].
export function templateSchemes(t) {
  const seen = new Set();
  for (const d of t.days) for (const e of d.exercises) seen.add(e.scheme || 'double');
  return [...seen];
}
